import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import GoogleMapsLoader from '../../components/GoogleMapsLoader';
import { Service, getServiceAddons } from '../../lib/services';
import { getServicesTitleTags, ServiceTitleTag } from '../../lib/serviceTitleTags';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { toast } from 'react-hot-toast';
import { useParams, useNavigate } from 'react-router-dom';
import type { Business } from '../../contexts/BusinessContext';
import { format } from 'date-fns';
import { createMobileBooking } from '../../lib/bookings';
import { findExistingCustomer, updateCustomerFromBooking } from '../../lib/customers';
import { triggerBookingAutomations } from '../../lib/automationsV2';
import RichTextDescription from '../../components/RichTextDescription';
import BookingPagePopup from '../../components/BookingPagePopup';
import { DiscountCodeService } from '../../services/discountCodeService';
import { BookingAccountButton } from '../../components/booking/BookingAccountButton';
import {
  applyBookingReward,
  bookingCheckoutSignup,
  bookingEmailStatus,
  portalPasswordLogin,
  saveBookingSession,
  type BookingHome,
  type BookingReward,
} from '../../lib/portal';

function bookingExtraOff(
  net: number,
  code: { type: 'percentage' | 'fixed'; value: number } | null,
  reward: BookingReward | null,
): number {
  if (code) {
    const amount = code.type === 'percentage' ? net * (code.value / 100) : code.value;
    return Math.min(Math.max(amount, 0), Math.max(net, 0));
  }
  if (!reward || reward.kind === 'wallet_credit') return 0;
  const amount = reward.kind === 'percent_discount'
    ? net * (Number(reward.percent_off) / 100)
    : Number(reward.value_cents || 0) / 100;
  return Math.min(Math.max(amount, 0), Math.max(net, 0));
}

interface PlaceResult {
  formatted_address: string;
  address_components?: {
    long_name: string;
    short_name: string;
    types: string[];
  }[];
}

// Add a declaration to extend the Window interface
declare global {
  interface Window {
    google: any;
    initGoogleMapsCallback?: () => void;
  }
}

interface Category {
  id: string;
  category_title: string;
  sub_categories?: SubCategory[];
  /** Optional tile image set in the online booking configuration. */
  image_url?: string | null;
}

interface SubCategory {
  id: string;
  title: string;
  services: Array<{
    service: Service;
    priceOption: {
      id: string;
      name: string;
      price: number;
      duration_minutes: number;
      compare_at_price?: number | null;
    };
  }>;
}

interface ResourceAvailability {
  id: string;
  name: string;
  first_name: string;
  last_name: string;
  profile_photo?: string;
  resource_id?: string;
  _isAsset?: boolean;
  _assetType?: string;
  postal_codes: {
    [key: string]: string[];
  };
  working_hours: {
    [key: string]: {
      areas: string[];
      radius: number;
      shifts: Array<{ start: string; end: string }>;
      enabled: boolean;
    };
  };
}

interface TimeSlot {
  time: Date;
  availableResources: Array<{
    id: string;
    resource_id?: string;
    name?: string;
    first_name: string;
    last_name: string;
    profile_photo?: string;
    _isAsset?: boolean;
    _assetType?: string;
  }>;
}

type Step = 'location' | 'category' | 'service' | 'datetime' | 'customer';

/** Out-of-area decline copy — the location step renders this one with a call button. */
const OUT_OF_SERVICE_AREA_MESSAGE = 'Looks like your address is outside our regular service area. Give us a call instead.';

/** Official multicolor Google "G" — used by the reviews trust badge. */
const GoogleGIcon = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true" focusable="false">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
  </svg>
);

const ASSET_AVAILABILITY_DAY_KEYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
] as const;

/** Build working_hours shape from asset_availability row (per-day jsonb columns). */
function mapAssetAvailabilityRowToWorkingHours(
  row: Record<string, unknown>
): ResourceAvailability['working_hours'] {
  const out: ResourceAvailability['working_hours'] = {};
  for (const day of ASSET_AVAILABILITY_DAY_KEYS) {
    const raw = row[day];
    const d =
      raw && typeof raw === 'object' && !Array.isArray(raw)
        ? (raw as Record<string, unknown>)
        : null;
    const shifts = Array.isArray(d?.shifts)
      ? (d.shifts as { start: string; end: string }[]).filter(
          (s) => s && typeof s.start === 'string' && typeof s.end === 'string'
        )
      : [];
    out[day] = {
      enabled: !!(d && d.enabled),
      shifts,
      areas: [],
      radius: 0,
    };
  }
  return out;
}

// Add this interface for existing bookings
interface ExistingBooking {
  id: string;
  resource_id: string;
  start_date: string;
  start_time: string;
  end_date: string;
  end_time: string;
}

// Add this interface for address components
interface AddressComponents {
  street_number: string;
  route: string;
  locality: string;
  administrative_area_level_1: string;
  postal_code: string;
  formatted_address: string;
}

// Add new interface for blocked time slots
interface BlockedTimeSlot {
  date: string;
  blockedSlots: string[];
}

interface BlockedTimeSlots {
  [resourceId: string]: BlockedTimeSlot[];
}

export default function BookOnline() {
  const [address, setAddress] = useState('');
  const [unitNumber, setUnitNumber] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [error, setError] = useState(''); // Fatal errors (business not found, connection issues)
  const [addressError, setAddressError] = useState(''); // Address validation errors
  const [isServiceable, setIsServiceable] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const [currentStep, setCurrentStep] = useState<Step>('location');
  const [categories, setCategories] = useState<Category[]>([]);
  const [isCategoriesLoading, setIsCategoriesLoading] = useState(false);
  const [isAddressValidating, setIsAddressValidating] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedService, setSelectedService] = useState<{
    service: Service;
    priceOption: {
      id: string;
      name: string;
      price: number;
      duration_minutes: number;
      compare_at_price?: number | null;
    };
  } | null>(null);
  const [selectedAddons, setSelectedAddons] = useState<any[]>([]);
  const [showAddonsModal, setShowAddonsModal] = useState(false);
  const [serviceAddons, setServiceAddons] = useState<any[]>([]);
  // Previous step/phase index for direction-aware step transitions
  const prevPhaseIndexRef = useRef(0);
  // Mobile: booking summary on the confirm step starts collapsed
  const [summaryExpanded, setSummaryExpanded] = useState(false);
  // Discount code input stays hidden behind a text link until tapped
  const [showDiscountField, setShowDiscountField] = useState(false);
  const [serviceTitleTags, setServiceTitleTags] = useState<ServiceTitleTag[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTime, setSelectedTime] = useState<Date | null>(null);
  const autoCompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [expandedSubCategory, setExpandedSubCategory] = useState<string | null>(null);
  // Multiple service descriptions can be open at once (Set of card keys).
  // Toggling expands/collapses the WHOLE visual row: in the two-column layout
  // the neighbor card stretches anyway, so opening its description too avoids
  // a tall empty card next to an expanded one.
  const [expandedServiceIds, setExpandedServiceIds] = useState<Set<string>>(new Set());
  const serviceCardKey = (svc: any) => `${svc.service.id}-${svc.priceOption.id}`;
  const toggleServiceRowExpanded = (list: any[], index: number) => {
    const twoCols = typeof window !== 'undefined' && window.innerWidth >= 768; // md: grid breakpoint
    const rowStart = twoCols ? index - (index % 2) : index;
    const rowIndexes = twoCols ? [rowStart, rowStart + 1].filter((i) => i < list.length) : [index];
    const targetKey = serviceCardKey(list[index]);
    setExpandedServiceIds(prev => {
      const next = new Set(prev);
      const expanding = !next.has(targetKey);
      for (const i of rowIndexes) {
        const key = serviceCardKey(list[i]);
        if (expanding) next.add(key);
        else next.delete(key);
      }
      return next;
    });
  };
  const [availableResources, setAvailableResources] = useState<ResourceAvailability[]>([]);
  const [availableTimeSlots, setAvailableTimeSlots] = useState<TimeSlot[]>([]);
  const [selectedResourceId, setSelectedResourceId] = useState<string | null>(null);
  const [selectedAssetId, setSelectedAssetId] = useState<string | null>(null);
  const [isResourceModalOpen, setIsResourceModalOpen] = useState(false);
  const [selectedTimeForModal, setSelectedTimeForModal] = useState<TimeSlot | null>(null);
  const [business, setBusiness] = useState<Business | null>(null);
  const businessRef = useRef<Business | null>(null);
  const { businessSlug, portalSlug } = useParams<{ businessSlug: string; portalSlug?: string }>();
  const [onlineBookingPortalId, setOnlineBookingPortalId] = useState<string | null>(null);
  const [portalAssignedAssetId, setPortalAssignedAssetId] = useState<string | null>(null);
  const [portalResolved, setPortalResolved] = useState(false);
  const navigate = useNavigate();
  const [businessTaxRates, setBusinessTaxRates] = useState<{ name: string; rate: number }[]>([]);
  const [serviceAdditionalTaxes, setServiceAdditionalTaxes] = useState<{ service_id: string; name: string; rate: number }[]>([]);
  
  // Keep ref in sync with state
  useEffect(() => {
    businessRef.current = business;
  }, [business]);

  useEffect(() => {
    if (!business?.id || !supabase) return;
    supabase
      .from('tax_rates')
      .select('name, rate')
      .eq('business_id', business.id)
      .then(({ data }) => { if (data) setBusinessTaxRates(data); });
  }, [business?.id]);

  useEffect(() => {
    if (!selectedService || !supabase) { setServiceAdditionalTaxes([]); return; }
    supabase
      .from('service_additional_taxes')
      .select('service_id, name, rate')
      .eq('service_id', selectedService.service.id)
      .then(({ data }) => { if (data) setServiceAdditionalTaxes(data); });
  }, [selectedService?.service?.id]);

  const combinedBusinessRate = businessTaxRates.reduce((sum, r) => sum + r.rate, 0) / 100;
  const businessTaxLabel = businessTaxRates.length > 0
    ? businessTaxRates.map(r => `${r.name} (${r.rate}%)`).join(' + ')
    : 'Tax';
  
  console.log('🚀 BookOnline component rendered - businessSlug:', businessSlug);
  console.log('🚀 Current business state:', business);
  console.log('🚀 isInitialized:', isInitialized);
  console.log('🚀 isLoading:', isLoading);
  
  // Popup settings state
  const [popupSettings, setPopupSettings] = useState({
    popup_enabled: false,
    popup_delay_seconds: 10,
    popup_duration_seconds: 5,
    popup_text: 'Welcome! Book your appointment today and get 10% off your first service.',
    popup_button_text: 'Book Now',
    popup_button_action: 'url' as 'url' | 'lead_form',
    popup_button_url: '',
    popup_send_email: false,
    popup_email_template_id: '',
    call_button_enabled: false
  });
  
  // Booking discount percentage state
  const [bookingDiscountPercentage, setBookingDiscountPercentage] = useState<number>(0);

  // Manual Google reviews trust badge (configured in online booking settings)
  const [googleReviewsBadge, setGoogleReviewsBadge] = useState<{
    enabled: boolean;
    rating: number | null;
    count: number | null;
    url: string | null;
  }>({ enabled: false, rating: null, count: null, url: null });
  
  // Add state for blocked time slots
  const [blockedTimeSlots, setBlockedTimeSlots] = useState<BlockedTimeSlots>({});

  // Add state for existing bookings
  const [existingBookings, setExistingBookings] = useState<ExistingBooking[]>([]);
  
  // Performance optimization: Cache for date availability checks
  const dateAvailabilityCache = useRef<Map<string, boolean>>(new Map());
  
  // Force calendar refresh counter
  const [calendarRefreshKey, setCalendarRefreshKey] = useState(0);
  
  // Loading state for resources
  const [isLoadingResources, setIsLoadingResources] = useState(false);
  
  // Add state for resource details
  const [resourceDetails, setResourceDetails] = useState<{[key: string]: {first_name: string; last_name: string; name?: string; avatar_url?: string}}>({});

  // Add buffer time state
  const [bufferTime, setBufferTime] = useState<number>(0);

  // Custom thank you URL state
  const [customThankYouUrl, setCustomThankYouUrl] = useState<string | null>(null);

  // Add session tracking state
  const [sessionId] = useState<string>(() => `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);

  // Add these state variables to the component
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [vehicleYear, setVehicleYear] = useState('');
  const [vehicleMake, setVehicleMake] = useState('');
  const [vehicleModel, setVehicleModel] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Discount code state
  const [discountCode, setDiscountCode] = useState('');
  const [appliedDiscount, setAppliedDiscount] = useState<{
    id: string;
    code: string;
    type: 'percentage' | 'fixed';
    value: number;
    description?: string;
  } | null>(null);
  const [discountValidating, setDiscountValidating] = useState(false);
  const [discountError, setDiscountError] = useState('');
  const [bookingCustomerId, setBookingCustomerId] = useState<string | null>(null);
  const [bookingVehicles, setBookingVehicles] = useState<BookingHome['vehicles']>([]);
  const [rewardCatalog, setRewardCatalog] = useState<BookingReward[]>([]);
  const [pointBalance, setPointBalance] = useState(0);
  const [selectedReward, setSelectedReward] = useState<BookingReward | null>(null);
  const [accountPassword, setAccountPassword] = useState('');
  const [emailAccountState, setEmailAccountState] = useState<'new' | 'account' | 'customer' | null>(null);
  const bookingCustomerRef = useRef<string | null>(null);
  const openAccountRef = useRef<((mode?: 'login' | 'signup') => void) | null>(null);
  
  // Map state - Start with default Vancouver coordinates
  const [mapCenter, setMapCenter] = useState<{ lat: number; lng: number }>({ lat: 49.2827, lng: -123.1207 });
  const [customerLocation, setCustomerLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [mapZoom, setMapZoom] = useState(12);
  const mapRef = useRef<google.maps.Map | null>(null);
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const businessMarkerRef = useRef<google.maps.Marker | null>(null);
  const customerMarkerRef = useRef<google.maps.Marker | null>(null);
  const [isMapAnimating, setIsMapAnimating] = useState(false);
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const [addressSelectedFromDropdown, setAddressSelectedFromDropdown] = useState(false);
  
  // DEBUG: Track state changes
  useEffect(() => {
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    console.log('🔵 STATE UPDATE DETECTED');
    console.log('   addressSelectedFromDropdown:', addressSelectedFromDropdown);
    console.log('   isServiceable:', isServiceable);
    console.log('   addressError:', addressError ? `"${addressError}"` : '(none)');
    console.log('   currentStep:', currentStep);
    
    const buttonShouldShow = currentStep === 'location' && addressSelectedFromDropdown && isServiceable === true;
    
    if (buttonShouldShow) {
      console.log('🟢 ✅ NEXT BUTTON SHOULD BE VISIBLE NOW!');
    } else {
      console.log('🔴 ❌ NEXT button hidden because:');
      if (currentStep !== 'location') console.log('      - Not on location step');
      if (!addressSelectedFromDropdown) console.log('      - addressSelectedFromDropdown is', addressSelectedFromDropdown);
      if (isServiceable !== true) console.log('      - isServiceable is', isServiceable);
    }
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
  }, [addressSelectedFromDropdown, isServiceable, currentStep, addressError]);
  
  // Service selection handler with add-ons check
  const handleServiceSelection = async (service: any) => {
    setSelectedService(service);
    // Clear cache immediately when service changes
    dateAvailabilityCache.current.clear();
    // Force calendar refresh
    setCalendarRefreshKey(prev => prev + 1);
    
    // Pre-fetch resources immediately when service is selected
    if (business && (postalCode || portalAssignedAssetId)) {
      await fetchAvailableResources();
    }
    
    try {
      // Check if this service has add-ons
      const addons = await getServiceAddons(service.service.id);
      setServiceAddons(addons);
      
      if (addons.length > 0) {
        // Service has add-ons — stay on the service step and swap the container
        // to the inline "Available Add-Ons" view (no modal).
        setSelectedAddons([]);
        setShowAddonsModal(true);
      } else {
        // No add-ons, proceed directly to datetime
        setCurrentStep('datetime');
      }
    } catch (error) {
      console.error('Error loading service add-ons:', error);
      // If there's an error loading add-ons, proceed to datetime anyway
      setCurrentStep('datetime');
    }
  };

  const handleAddonsSelection = (selectedAddonsList: any[]) => {
    setSelectedAddons(selectedAddonsList);
    setShowAddonsModal(false);
    // Clear cache before navigating to datetime step
    dateAvailabilityCache.current.clear();
    // Force calendar refresh
    setCalendarRefreshKey(prev => prev + 1);
    setCurrentStep('datetime');
  };

  // Discount code validation function
  const validateDiscountCode = async (code: string) => {
    if (!code.trim()) {
      setAppliedDiscount(null);
      setDiscountError('');
      return;
    }

    if (!business?.id || !selectedService) {
      setDiscountError('Business information not available');
      return;
    }

    setDiscountValidating(true);
    setDiscountError('');

    try {
      const orderAmount = selectedService.priceOption.price;
      const result = await DiscountCodeService.validateDiscountCode(business.id, code, orderAmount);
      
      if (result.isValid && result.code) {
        setAppliedDiscount({
          id: result.code.id,
          code: result.code.code,
          type: result.code.type,
          value: result.code.value,
          description: result.code.description
        });
        setSelectedReward(null);
        setDiscountError('');
      } else {
        setAppliedDiscount(null);
        setDiscountError(result.error || 'Invalid discount code');
      }
    } catch (error) {
      console.error('Error validating discount code:', error);
      setAppliedDiscount(null);
      setDiscountError('Error validating discount code');
    } finally {
      setDiscountValidating(false);
    }
  };

  const handleBookingCustomer = useCallback((home: BookingHome | null) => {
    if (!home) {
      bookingCustomerRef.current = null;
      setBookingCustomerId(null);
      setBookingVehicles([]);
      setRewardCatalog([]);
      setPointBalance(0);
      setSelectedReward(null);
      return;
    }
    const switched = bookingCustomerRef.current !== home.profile.id;
    bookingCustomerRef.current = home.profile.id;
    setBookingCustomerId(home.profile.id);
    setBookingVehicles(home.vehicles || []);
    setPointBalance(home.points || 0);
    const bookable = (home.rewards || []).filter((reward) => reward.kind !== 'wallet_credit');
    setRewardCatalog(bookable);
    const held = bookable.find((reward) => reward.id === home.held?.redemption_id) || null;
    if (!switched) {
      if (held) setSelectedReward(held);
      return;
    }
    setFirstName(home.profile.first_name || '');
    setLastName(home.profile.last_name || '');
    setEmail(home.profile.email || '');
    setPhone(home.profile.phone || '');
    setAccountPassword('');
    setEmailAccountState(null);
    if (home.profile.address) {
      setAddress((prev) => prev || home.profile.address || '');
    }
    const vehicles = home.vehicles || [];
    const chosen = vehicles.find((vehicle) => vehicle.is_primary) || (vehicles.length === 1 ? vehicles[0] : null);
    if (chosen) {
      setVehicleYear(String(chosen.year || ''));
      setVehicleMake(chosen.make || '');
      setVehicleModel(chosen.model || '');
    }
    setSelectedReward(held);
  }, []);

  useEffect(() => {
    if (bookingCustomerId || !businessSlug) return;
    const password = accountPassword.trim();
    const mail = email.trim();
    if (!password || !mail.includes('@')) {
      setEmailAccountState(null);
      return;
    }
    let cancel = false;
    const timer = window.setTimeout(() => {
      bookingEmailStatus(businessSlug, mail)
        .then((state) => { if (!cancel) setEmailAccountState(state); })
        .catch(() => { if (!cancel) setEmailAccountState(null); });
    }, 400);
    return () => {
      cancel = true;
      window.clearTimeout(timer);
    };
  }, [accountPassword, email, bookingCustomerId, businessSlug]);

  const [formErrors, setFormErrors] = useState({
    firstName: '',
    lastName: '',
    vehicleYear: '',
    vehicleMake: '',
    vehicleModel: '',
    email: '',
    phone: ''
  });

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();
  };

  useEffect(() => {
    console.log('🔄 useEffect RUNNING - Loading business...', { businessSlug, isInitialized, hasSupabase: !!supabase });
    
    if (!businessSlug) {
      console.log('❌ No businessSlug, redirecting');
      navigate('/', { replace: true });
      return;
    }

    if (!supabase) {
      console.error('❌ Supabase client is not initialized');
      setError('Unable to connect to the database');
      setIsLoading(false);
      return;
    }
    
    console.log('✅ Checks passed, proceeding to load business...');

    // Immediately show the UI if we have cached business data
    const cachedBusiness = sessionStorage.getItem(`business_${businessSlug}`);
    console.log('📦 Checking cache for business_' + businessSlug + ':', cachedBusiness ? 'FOUND' : 'NOT FOUND');
    
    if (cachedBusiness && !isInitialized) {
      try {
        const data = JSON.parse(cachedBusiness);
        console.log('📦 Using cached business data:', data);
        console.log('📦 Cached business ID:', data.id);
        setBusiness(data);
        setIsLoading(false);
        setIsInitialized(true);
        
        // Background refresh
        refreshBusinessData();
        return;
      } catch (e) {
        console.log('❌ Invalid cached business data, fetching fresh');
      }
    } else if (cachedBusiness) {
      console.log('⏭️ Cache found but already initialized, skipping');
    }

    async function loadBusiness() {
      try {
        console.log('📡 Loading business with slug:', businessSlug);
        const { data, error: fetchError } = await supabase
          .from('businesses')
          .select('id, name, slug, phone, address, booking_settings')
          .eq('slug', businessSlug)
          .single();

        console.log('📡 Business query result:', { data, error: fetchError });

        if (fetchError || !data) {
          console.error('❌ Error fetching business:', fetchError);
          setError('Business not found');
          setIsLoading(false);
          return;
        }

        console.log('✅ Business loaded successfully:', data);
        console.log('✅ Business ID:', data.id);

        // Cache the business data
        sessionStorage.setItem(`business_${businessSlug}`, JSON.stringify(data));
        
        setBusiness(data);
        setIsLoading(false);
        setIsInitialized(true);
        
      } catch (err) {
        console.error('Error loading business:', err);
        setError('Failed to load business information');
        setIsLoading(false);
      }
    }

    async function refreshBusinessData() {
      try {
        const { data } = await supabase
          .from('businesses')
          .select('id, name, slug, phone, address, booking_settings')
          .eq('slug', businessSlug)
          .single();
        
        if (data) {
          sessionStorage.setItem(`business_${businessSlug}`, JSON.stringify(data));
          setBusiness(data);
        }
      } catch (e) {
        console.log('Background refresh failed, using cached data');
      }
    }

    if (!isInitialized) {
      console.log('🚀 Calling loadBusiness() now...');
      loadBusiness();
    } else {
      console.log('⏭️ Skipping loadBusiness - already initialized');
    }
  }, [businessSlug, portalSlug, navigate, isInitialized]);

  // Add function to fetch buffer time and popup settings (scoped to online booking portal)
  const fetchBufferTime = async (portalId: string) => {
    if (!supabase) return;
    
    try {
      console.log('🔍 Fetching online_booking_settings for portal ID:', portalId);
      const { data, error } = await supabase
        .from('online_booking_settings')
        .select('business_id, buffer_time, popup_enabled, popup_delay_seconds, popup_duration_seconds, popup_text, popup_button_text, popup_button_action, popup_button_url, popup_send_email, popup_email_template_id, call_button_enabled, custom_thank_you_url, booking_discount_percentage, google_reviews_badge_enabled, google_reviews_rating, google_reviews_count, google_reviews_url')
        .eq('portal_id', portalId)
        .single();
      
      console.log('📊 Query result:', { data, error });
      
      if (error) {
        console.error('❌ Error loading online booking settings:', error);
        if (error.code === 'PGRST116') {
          console.log('⚠️ No online_booking_settings record exists for this business');
          setBufferTime(0);
          setBookingDiscountPercentage(0);
        } else {
          setBufferTime(0);
          setBookingDiscountPercentage(0);
        }
        return;
      }
      
      console.log('✅ Loaded online_booking_settings:', data);
      console.log('📞 call_button_enabled from database:', data?.call_button_enabled);
      console.log('💰💰💰 DISCOUNT % FROM DB:', data?.booking_discount_percentage, typeof data?.booking_discount_percentage);
      
      // Set buffer time
      if (data?.buffer_time !== undefined && data?.buffer_time !== null) {
        setBufferTime(data.buffer_time);
      } else {
        setBufferTime(0);
      }
      
      // Set booking discount percentage
      if (data?.booking_discount_percentage !== undefined && data?.booking_discount_percentage !== null) {
        setBookingDiscountPercentage(data.booking_discount_percentage);
      } else {
        setBookingDiscountPercentage(0);
      }
      
      // Set popup settings
      setPopupSettings({
        popup_enabled: data?.popup_enabled || false,
        popup_delay_seconds: data?.popup_delay_seconds || 10,
        popup_duration_seconds: data?.popup_duration_seconds || 5,
        popup_text: data?.popup_text || 'Welcome! Book your appointment today and get 10% off your first service.',
        popup_button_text: data?.popup_button_text || 'Book Now',
        popup_button_action: (data?.popup_button_action as 'url' | 'lead_form') || 'url',
        popup_button_url: data?.popup_button_url || '',
        popup_send_email: data?.popup_send_email || false,
        popup_email_template_id: data?.popup_email_template_id || '',
        call_button_enabled: data?.call_button_enabled || false
      });
      
      // Set Google reviews badge (manual trust badge on the location step)
      setGoogleReviewsBadge({
        enabled: data?.google_reviews_badge_enabled || false,
        rating: data?.google_reviews_rating != null ? Number(data.google_reviews_rating) : null,
        count: data?.google_reviews_count ?? null,
        url: data?.google_reviews_url || null,
      });

      // Set custom thank you URL
      setCustomThankYouUrl(data?.custom_thank_you_url || null);
      console.log('🔗 Custom thank you URL:', data?.custom_thank_you_url || 'Not set - will use default');
      
      console.log('📞 Final call_button_enabled state:', data?.call_button_enabled || false);
    } catch (err) {
      setBufferTime(0);
      setBookingDiscountPercentage(0);
    }
  };

  // Function to track visits to the online booking form
  const trackBookingVisit = async (businessId: string) => {
    if (!supabase) return;
    
    try {
      // Gather visit information
      const visitData = {
        business_id: businessId,
        session_id: sessionId,
        step: 'location', // Initial step
        ip_address: null, // We can't get this from the frontend for privacy reasons
        user_agent: navigator.userAgent,
        referrer: document.referrer || null
      };

      const { error } = await supabase
        .from('online_booking_visits')
        .insert(visitData);

      if (error) {
        console.error('Error tracking booking visit:', error);
      } else {
        console.log('Online booking visit tracked successfully');
      }
    } catch (err) {
      console.error('Error tracking booking visit:', err);
    }
  };

  // Function to track step progression in the booking funnel
  const trackStepProgression = async (businessId: string, step: string) => {
    if (!supabase) return;
    
    try {
      const stepData = {
        business_id: businessId,
        session_id: sessionId,
        step: step,
        user_agent: navigator.userAgent,
        referrer: document.referrer || null
      };

      const { error } = await supabase
        .from('booking_step_progression')
        .insert(stepData);

      if (error) {
        console.error('Error tracking step progression:', error);
      } else {
        console.log(`Step progression tracked: ${step}`);
      }
    } catch (err) {
      console.error('Error tracking step progression:', err);
    }
  };

  // Resolve which portal this URL refers to, then load its settings
  useEffect(() => {
    if (!business?.id || !supabase) return;
    setOnlineBookingPortalId(null);
    setPortalAssignedAssetId(null);
    setPortalResolved(false);
    let cancelled = false;
    (async () => {
      const colsFull = 'id, public_slug, display_name, is_default, assigned_asset_id';
      const colsBase = 'id, public_slug, display_name, is_default';
      const run = (cols: string) =>
        portalSlug
          ? supabase
              .from('online_booking_portals')
              .select(cols)
              .eq('business_id', business.id)
              .eq('public_slug', portalSlug)
              .maybeSingle()
          : supabase
              .from('online_booking_portals')
              .select(cols)
              .eq('business_id', business.id)
              .eq('is_default', true)
              .maybeSingle();

      let { data, error } = await run(colsFull);
      if (error) {
        const retry = await run(colsBase);
        data = retry.data
          ? { ...retry.data, assigned_asset_id: null as string | null }
          : null;
        error = retry.error;
      }
      if (cancelled) return;
      if (error || !data) {
        setError(
          portalSlug
            ? 'This booking link is not available.'
            : 'Online booking is not set up for this business yet.'
        );
        setIsLoading(false);
        setPortalResolved(true);
        return;
      }
      setError('');
      setOnlineBookingPortalId(data.id);
      const assetId = data.assigned_asset_id ?? null;
      setPortalAssignedAssetId(assetId);
      if (assetId) {
        setCurrentStep('category');
        setIsServiceable(true);
        setAddressSelectedFromDropdown(true);
        setAddress('');
        setPostalCode('');
        setUnitNumber('');
      }
      await fetchBufferTime(data.id);
      await trackBookingVisit(business.id);
      setPortalResolved(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [business?.id, portalSlug, supabase]);

  useEffect(() => {
    // Check if Google Maps API key is available
    const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
    if (!apiKey) {
      console.error('Google Maps API key is not set');
      setError('Google Maps API key is missing');
      return;
    }

    // Clean up any existing Google Maps script first to prevent conflicts
    const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
    if (existingScript) {
      existingScript.remove();
      console.log('Removed existing Google Maps script');
    }

    // Create a global callback function that will be called when the API is loaded
    window.initGoogleMapsCallback = () => {
      console.log('Google Maps API loaded successfully via callback');
      
      // Small delay to ensure the API is fully initialized
      setTimeout(() => {
        initAutocomplete();
        console.log('Autocomplete initialization triggered from callback');
      }, 100);
    };

    // Load the script with a callback
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&callback=initGoogleMapsCallback`;
    script.async = true;
    script.defer = true;
    script.id = 'google-maps-script';
    
    script.onerror = () => {
      console.error('Failed to load Google Maps API');
      setError('Unable to load address autocomplete');
    };
    
    document.head.appendChild(script);
    console.log('Added Google Maps script to head with callback');

    // Set up a retry mechanism in case the callback doesn't fire
    const retryTimeout = setTimeout(() => {
      if (window.google?.maps?.places) {
        console.log('Google Maps already loaded but callback not fired, initializing manually');
        initAutocomplete();
      } else {
        console.log('Google Maps failed to load within timeout, retrying...');
        // Remove script and try again
        document.querySelector('#google-maps-script')?.remove();
        document.head.appendChild(script);
      }
    }, 3000);

    return () => {
      // Clean up global callback
      if (window.initGoogleMapsCallback) {
        delete window.initGoogleMapsCallback;
      }
      
      // Clear retry timeout
      clearTimeout(retryTimeout);
      
      // Clean up existing autocomplete instance
      if (autoCompleteRef.current) {
        google.maps.event.clearInstanceListeners(autoCompleteRef.current);
        autoCompleteRef.current = null;
      }
    };
  }, []);

  // Add a new effect to reinitialize autocomplete when returning to the location step
  useEffect(() => {
    // When returning to the location step, reinitialize autocomplete if Google Maps is loaded
    if (currentStep === 'location' && window.google?.maps?.places && inputRef.current) {
      console.log('Returning to location step, reinitializing autocomplete');
      initAutocomplete();
    }
  }, [currentStep]);

  useEffect(() => {
    if (currentStep === 'category' && business?.id && onlineBookingPortalId) {
      fetchCategories();
    }
  }, [currentStep, business?.id, onlineBookingPortalId]);

  useEffect(() => {
    setCategories([]);
  }, [onlineBookingPortalId]);
  
  // Initialize Google Map when loaded
  useEffect(() => {
    if (isMapLoaded && mapContainerRef.current && window.google?.maps && !mapRef.current) {
      console.log('🗺️ Initializing Google Map');
      
      const map = new window.google.maps.Map(mapContainerRef.current, {
        center: mapCenter,
        zoom: mapZoom,
        disableDefaultUI: true,
        gestureHandling: 'none',
        zoomControl: false,
        scrollwheel: false,
        disableDoubleClickZoom: true,
        draggable: false,
        styles: [
          // Warm beige/tan palette
          {
            featureType: 'all',
            elementType: 'all',
            stylers: [{ saturation: -50 }, { lightness: 10 }] // Less desaturated for more warmth
          },
          {
            featureType: 'water',
            elementType: 'geometry',
            stylers: [{ color: '#d4e3ed' }] // Soft blue water with more saturation
          },
          {
            featureType: 'landscape',
            elementType: 'geometry',
            stylers: [{ color: '#f7f4f0' }] // Warm sandy beige land
          },
          {
            featureType: 'road.highway',
            elementType: 'geometry',
            stylers: [{ color: '#fefefe' }] // Bright white highways
          },
          {
            featureType: 'road.arterial',
            elementType: 'geometry',
            stylers: [{ color: '#ffffff' }] // White arterial roads
          },
          {
            featureType: 'road.local',
            elementType: 'geometry',
            stylers: [{ color: '#fcfcfc' }] // Slightly off-white local roads
          },
          {
            featureType: 'road',
            elementType: 'geometry.stroke',
            stylers: [{ color: '#ebe6de' }] // Warm tan road outlines
          },
          {
            featureType: 'road',
            elementType: 'labels.text.fill',
            stylers: [{ color: '#9a8a7a' }] // Warm taupe road text
          },
          {
            featureType: 'poi',
            elementType: 'labels',
            stylers: [{ visibility: 'off' }]
          },
          {
            featureType: 'transit',
            elementType: 'labels',
            stylers: [{ visibility: 'off' }]
          },
          {
            featureType: 'administrative',
            elementType: 'labels.text.fill',
            stylers: [{ color: '#c4b8a8' }] // Warm beige for city names
          },
          {
            featureType: 'poi.park',
            elementType: 'geometry',
            stylers: [{ color: '#ebe9e4' }] // Subtle warm grey-green parks
          }
        ]
      });
      
      mapRef.current = map;
      console.log('✅ Google Map initialized at:', mapCenter);
    }
  }, [isMapLoaded]);
  
  // Update map center when it changes
  useEffect(() => {
    if (mapRef.current && mapCenter) {
      mapRef.current.setCenter(mapCenter);
      console.log('📍 Map centered at:', mapCenter);
    }
  }, [mapCenter]);
  
  // Update customer marker when location changes
  useEffect(() => {
    if (customerLocation && mapRef.current && window.google?.maps) {
      console.log('📍 Adding customer marker at:', customerLocation);
      
      // Remove old customer marker if it exists
      if (customerMarkerRef.current) {
        customerMarkerRef.current.setMap(null);
      }
      
      // Create large, prominent red pin marker using default Google pin
      // Create custom high-quality SVG pin
      const svgPin = `
        <svg width="48" height="64" viewBox="0 0 48 64" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
              <feDropShadow dx="0" dy="2" stdDeviation="3" flood-opacity="0.4"/>
            </filter>
          </defs>
          <path d="M24 0C10.745 0 0 10.745 0 24c0 18 24 40 24 40s24-22 24-40C48 10.745 37.255 0 24 0z" 
                fill="#EF4444" 
                filter="url(#shadow)"/>
          <circle cx="24" cy="24" r="10" fill="white"/>
          <circle cx="24" cy="24" r="6" fill="#EF4444"/>
        </svg>
      `;
      
      const marker = new window.google.maps.Marker({
        position: customerLocation,
        map: mapRef.current,
        icon: {
          url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svgPin),
          scaledSize: new window.google.maps.Size(48, 64),
          anchor: new window.google.maps.Point(24, 64)
        },
        title: 'Your Service Location',
        animation: window.google.maps.Animation.DROP,
        zIndex: 999
      });
      
      customerMarkerRef.current = marker;
      console.log('✅ Customer red pin marker added');
    }
  }, [customerLocation]);

  // Prefetch categories when on location step to improve transition smoothness
  useEffect(() => {
    if (currentStep === 'location' && business?.id && onlineBookingPortalId && isServiceable && categories.length === 0) {
      fetchCategories();
    }
  }, [currentStep, business?.id, onlineBookingPortalId, isServiceable, categories.length]);

  // Load title tags when categories are loaded
  useEffect(() => {
    if (categories.length > 0) {
      fetchServiceTitleTags();
    }
  }, [categories]);

  // Track step progression whenever currentStep changes
  useEffect(() => {
    if (business?.id && currentStep) {
      trackStepProgression(business.id, currentStep);
    }
  }, [currentStep, business?.id]);

  // Scroll to top when step changes for better UX
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStep]);

  const fetchCategories = async () => {
    try {
      setIsCategoriesLoading(true);
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }
      
      if (!business?.id) {
        throw new Error('Business not loaded');
      }
      if (!onlineBookingPortalId) {
        throw new Error('Booking portal not loaded');
      }
      
      const { data, error } = await supabase
        .from('online_booking_flow')
        .select('id, category_title, sub_categories, image_url')
        .eq('business_id', business.id)
        .eq('portal_id', onlineBookingPortalId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      const cats = (data || []) as any[];

      // Live-sync compare_at_price from the price_options catalog. The online
      // booking flow stores a SNAPSHOT of each service/price option, and editing
      // a service's pricing deletes + recreates its price_options with new ids,
      // so the snapshotted priceOption.id is often stale. Compare-at must always
      // reflect the live catalog, so we resolve by the stable service.id + option
      // name (falling back to id, then the sole option). Display-only; public RLS
      // already allows reading price_options.
      try {
        const serviceIds = new Set<string>();
        cats.forEach(cat => (cat.sub_categories || []).forEach((sub: any) => (sub.services || []).forEach((s: any) => {
          const sid = s?.service?.id;
          if (sid) serviceIds.add(sid);
        })));
        if (serviceIds.size > 0) {
          const { data: opts } = await supabase
            .from('price_options')
            .select('id, service_id, name, compare_at_price')
            .in('service_id', Array.from(serviceIds));
          if (opts && opts.length > 0) {
            const byService = new Map<string, any[]>();
            opts.forEach((o: any) => {
              const arr = byService.get(o.service_id) || [];
              arr.push(o);
              byService.set(o.service_id, arr);
            });
            const resolveCompareAt = (s: any): number | null => {
              const sid = s?.service?.id;
              const po = s?.priceOption;
              if (!sid || !po) return null;
              const options = byService.get(sid) || [];
              if (options.length === 0) return null;
              const match = options.find((o: any) => o.id === po.id)
                || options.find((o: any) => (o.name || '') === (po.name || ''))
                || (options.length === 1 ? options[0] : null);
              return match ? (match.compare_at_price ?? null) : null;
            };
            cats.forEach(cat => (cat.sub_categories || []).forEach((sub: any) => (sub.services || []).forEach((s: any) => {
              if (s?.priceOption) s.priceOption.compare_at_price = resolveCompareAt(s);
            })));
          }
        }
      } catch (mergeErr) {
        console.warn('compare-at merge skipped:', mergeErr);
      }

      setCategories(cats);
    } catch (err) {
      console.error('Error fetching categories:', err);
      setError('Unable to load service categories');
    } finally {
      setIsCategoriesLoading(false);
    }
  };

  const fetchServiceTitleTags = async () => {
    try {
      if (!categories || categories.length === 0) return;
      
      // Extract all service IDs from all categories and subcategories
      const serviceIds: string[] = [];
      categories.forEach(category => {
        if (category.sub_categories) {
          category.sub_categories.forEach(subCategory => {
            subCategory.services.forEach(service => {
              serviceIds.push(service.service.id);
            });
          });
        }
      });
      
      if (serviceIds.length > 0) {
        const titleTags = await getServicesTitleTags(serviceIds);
        setServiceTitleTags(titleTags);
      }
    } catch (err) {
      console.error('Error fetching service title tags:', err);
      // Don't show error to user, title tags are optional
    }
  };

  // Helper function to get title tag for a service
  const getServiceTitleTag = (serviceId: string) => {
    return serviceTitleTags.find(tag => tag.service_id === serviceId && tag.title_tag_enabled);
  };

  // Helper function to get service meter for a service
  const getServiceMeter = (serviceId: string) => {
    return serviceTitleTags.find(tag => tag.service_id === serviceId && tag.service_meter_enabled);
  };

  const initAutocomplete = () => {
    if (!inputRef.current || !window.google?.maps?.places) {
      console.error('Input ref or Google Places not ready');
      return;
    }

    try {
      console.log('Initializing Google Maps Autocomplete...');
      
      // Clear any existing listeners to prevent duplicates
      if (autoCompleteRef.current) {
        console.log('Cleaning up existing autocomplete instance');
        google.maps.event.clearInstanceListeners(autoCompleteRef.current);
        autoCompleteRef.current = null;
      }
      
      // Create the autocomplete instance with specific fields
      autoCompleteRef.current = new window.google.maps.places.Autocomplete(inputRef.current, {
        componentRestrictions: { country: 'ca' },
        fields: [
          'address_components',
          'formatted_address',
          'geometry',
          'place_id'
        ],
        types: ['address']
      });

      // Add the place_changed listener
      if (autoCompleteRef.current) {
        autoCompleteRef.current.addListener('place_changed', () => {
          const place = autoCompleteRef.current?.getPlace();
          console.log('📍 Selected place:', place);

          // CLEAR ALL STATE FIRST - fresh start
          console.log('🧹 Clearing previous state...');
          setAddressError('');
          setIsServiceable(null);
          setAddressSelectedFromDropdown(false); // Stay FALSE until validation passes
          setCustomerLocation(null);
          setIsMapAnimating(false);

          if (!place?.address_components) {
            setAddressError('Please select a valid address from the dropdown');
            return;
          }
          
          console.log('📍 Address clicked from dropdown - starting validation...');
          
          // Store location for animation AFTER validation
          if (place.geometry?.location) {
            const lat = place.geometry.location.lat();
            const lng = place.geometry.location.lng();
            // Save to window for later use
            (window as any).pendingMapAnimation = { lat, lng };
            console.log('📌 Stored location for post-validation animation:', { lat, lng });
          }

          // Initialize address components
          const addressComponents: AddressComponents = {
            street_number: '',
            route: '',
            locality: '',
            administrative_area_level_1: '',
            postal_code: '',
            formatted_address: place.formatted_address || ''
          };

          // Extract each component - improved extraction logic
          place.address_components.forEach(component => {
            // Check all types this component might match
            component.types.forEach(type => {
              // Handle postal_code specifically
              if (type === 'postal_code') {
                addressComponents.postal_code = component.long_name;
              } 
              // Handle other known types
              else if (type === 'street_number') {
                addressComponents.street_number = component.long_name;
              }
              else if (type === 'route') {
                addressComponents.route = component.long_name;
              }
              else if (type === 'locality') {
                addressComponents.locality = component.long_name;
              }
              else if (type === 'administrative_area_level_1') {
                addressComponents.administrative_area_level_1 = component.short_name;
              }
            });
          });

          console.log('Extracted address components:', addressComponents);

          // If we don't have a postal code, attempt to fetch it with a Places Details request
          if (!addressComponents.postal_code && place.place_id) {
            console.log('Postal code missing, attempting to fetch with Places Details');
            const placesService = new google.maps.places.PlacesService(document.createElement('div'));
            
            placesService.getDetails(
              { 
                placeId: place.place_id,
                fields: ['address_components']
              },
              (detailedPlace, status) => {
                if (status === google.maps.places.PlacesServiceStatus.OK && detailedPlace?.address_components) {
                  console.log('Got detailed place info:', detailedPlace);
                  
                  // Extract postal code from detailed place
                  let postalCode = '';
                  detailedPlace.address_components.forEach(component => {
                    if (component.types.includes('postal_code')) {
                      postalCode = component.long_name;
                    }
                  });
                  
                  if (postalCode) {
                    console.log('Found postal code in detailed request:', postalCode);
                    addressComponents.postal_code = postalCode;
                    processAddress(addressComponents);
                  } else {
                    setAddressError('Unable to find postal code for this address. Please enter a complete address.');
                    setIsServiceable(null);
                  }
                } else {
                  console.error('Failed to get detailed place info:', status);
                  setAddressError('Unable to verify the complete address. Please try again or enter a different address.');
                  setIsServiceable(null);
                }
              }
            );
          } else {
            // We have all components, process the address
            processAddress(addressComponents);
          }
        });
      }

      console.log('Autocomplete initialization complete');
    } catch (err) {
      console.error('Error initializing Google Places Autocomplete:', err);
      setError('Unable to initialize address autocomplete');
    }
  };

  // Separate function to process address after components are extracted
  const processAddress = (addressComponents: AddressComponents) => {
    try {
      console.log('Processing address with components:', addressComponents);
      
      // Ensure postal code is properly formatted if present
      if (addressComponents.postal_code) {
        // Remove spaces and convert to uppercase for Canadian postal codes
        addressComponents.postal_code = addressComponents.postal_code
          .replace(/\s+/g, '')
          .toUpperCase()
          .match(/^[A-Z]\d[A-Z]\d[A-Z]\d$/) 
            // Add a space in the middle if it's a valid Canadian postal code
            ? `${addressComponents.postal_code.slice(0, 3)} ${addressComponents.postal_code.slice(3)}`
            : addressComponents.postal_code;
      }

      // Validate required components
      if (!addressComponents.street_number || !addressComponents.route || 
          !addressComponents.locality || !addressComponents.administrative_area_level_1 || 
          !addressComponents.postal_code) {
        setAddressError('Please select a complete address with all components including postal code');
        setIsServiceable(null);
        return;
      }

      // Format the address consistently
      const formattedAddress = `${addressComponents.street_number} ${addressComponents.route}, ${addressComponents.locality}, ${addressComponents.administrative_area_level_1} ${addressComponents.postal_code}`;

      // Update state with the complete address
      setAddressError('');
      setAddress(formattedAddress);
      // Keep postal code in raw format (no spaces) for lookup
      const rawPostalCode = addressComponents.postal_code.replace(/\s+/g, '');
      setPostalCode(rawPostalCode);

      // If there's a unit number, prepend it
      if (unitNumber) {
        setAddress(`Unit ${unitNumber}, ${formattedAddress}`);
      }

      // Check if the postal code is serviceable - THIS RUNS ASYNC!
      console.log('🚀 CALLING checkPostalCodeAvailability...');
      checkPostalCodeAvailability(addressComponents.postal_code);
      console.log('⚡ checkPostalCodeAvailability called (but not finished yet!)');

      console.log('Final address set:', {
        fullAddress: formattedAddress,
        postalCode: addressComponents.postal_code,
        rawPostalCode,
        unitNumber: unitNumber || 'N/A'
      });
    } catch (err) {
      console.error('Error processing address:', err);
      setAddressError('Error processing the selected address. Please try again.');
      setIsServiceable(null);
    }
  };

  // Add this function to handle unit number changes
  const handleUnitNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setUnitNumber(value);
    
    // If we already have an address, update it with the new unit number
    if (address) {
      const baseAddress = address.replace(/^Unit \d+,\s*/, ''); // Remove existing unit number if any
      setAddress(value ? `Unit ${value}, ${baseAddress}` : baseAddress);
    }
  };

  const checkPostalCodeAvailability = async (postalCode: string) => {
    // Use REF to get CURRENT business state (not stale closure!)
    console.log('🔍 Business state check (using REF):', { 
      businessExists: !!businessRef.current, 
      businessId: businessRef.current?.id,
      businessName: businessRef.current?.name
    });
    
    // Wait for business to load if it's not ready yet
    if (!businessRef.current?.id) {
      console.log('⏳ Business not loaded yet, waiting...');
      setIsAddressValidating(true);
      
      // Wait up to 10 seconds for business to load - check REF each time!
      let attempts = 0;
      while (!businessRef.current?.id && attempts < 100) {
        await new Promise(resolve => setTimeout(resolve, 100));
        attempts++;
        if (attempts % 10 === 0) {
          console.log(`⏳ Still waiting... (${attempts/10}s) Business:`, businessRef.current);
        }
      }
      
      if (!businessRef.current?.id) {
        console.error('❌ Business failed to load after 10 seconds');
        console.error('❌ Final business state:', businessRef.current);
        setIsAddressValidating(false);
        setAddressError('Unable to verify service area. Please refresh the page.');
        return false;
      }
      console.log('✅ Business loaded!', businessRef.current);
    }
    
    if (!supabase) {
      console.error('❌ Supabase not initialized');
      setAddressError('Unable to connect to database');
      return false;
    }
    
    try {
      console.log('🔍 Starting postal code check...');
      setIsAddressValidating(true);
      setIsServiceable(null);
      setAddressError('');
      // Get first 3 characters of postal code
      const postalPrefix = postalCode.substring(0, 3).toUpperCase();
      
      const businessId = businessRef.current!.id;
      console.log('🔍 Checking postal code:', postalPrefix, 'for business:', businessId);

      // Query mobile_availability table for THIS business only
      console.log('📡 Executing Supabase query...');
      const { data, error } = await supabase
        .from('mobile_availability')
        .select('postal_codes, resources(first_name, last_name)')
        .eq('mobile_available', true)
        .eq('business_id', businessId); // ← Use businessRef!

      console.log('📡 Query completed!', { error, dataLength: data?.length });

      if (error) {
        console.error('❌ Supabase query error:', error);
        throw error;
      }

      console.log('📊 Found', data?.length || 0, 'mobile resources for this business');
      
      // Detailed logging for debugging
      if (data && data.length > 0) {
        console.log('🔍 DETAILED DEBUG - All mobile_availability records:', JSON.stringify(data, null, 2));
        data.forEach((record, index) => {
          console.log(`📋 Record ${index + 1}:`, {
            postal_codes: record.postal_codes,
            resource: record.resources,
            postal_codes_type: typeof record.postal_codes,
            postal_codes_keys: record.postal_codes ? Object.keys(record.postal_codes) : 'null'
          });
        });
      } else {
        console.warn('⚠️ NO mobile_availability records found for business:', businessId);
        console.warn('⚠️ This could mean:');
        console.warn('   1. No resources have mobile_available = true');
        console.warn('   2. No mobile_availability records exist for this business');
        console.warn('   3. Resources exist but postal codes were never assigned');
      }

      // Helper to check if any record serves this postal prefix
      const postalMatchesAny = (records: any[]) => records?.some(record => {
        const postalCodes = record.postal_codes as Record<string, string[]>;
        return Object.values(postalCodes || {}).some(codes => {
          if (!codes || !Array.isArray(codes)) return false;
          return codes.some(code => {
            const normalizedCode = (code || '').toString().toUpperCase().trim().substring(0, 3);
            const normalizedPrefix = postalPrefix.toUpperCase().trim().substring(0, 3);
            return normalizedCode === normalizedPrefix;
          });
        });
      });

      // Check resources
      const isResourceAvailable = postalMatchesAny(data || []);

      // Also check assets via mobile_asset_availability
      let isAssetAvailable = false;
      try {
        const { data: assetData, error: assetError } = await supabase
          .from('mobile_asset_availability')
          .select('postal_codes')
          .eq('mobile_available', true)
          .eq('business_id', businessId);

        if (!assetError && assetData) {
          isAssetAvailable = postalMatchesAny(assetData);
          console.log('📦 Asset postal code check:', { found: assetData.length, isAssetAvailable });
        }
      } catch {
        // Non-critical — continue with resource-only check
      }

      const isAvailable = isResourceAvailable || isAssetAvailable;
      const result = !!isAvailable;
      console.log('✅ Postal code validation complete:', {
        postalCode: postalPrefix,
        isServiceable: result
      });
      
      setIsServiceable(result);
      
      if (!isAvailable) {
        // INVALID - Show error, NO animation, NO button
        console.log('❌ Address NOT serviceable');
        console.log('   → Setting addressError');
        setAddressError(OUT_OF_SERVICE_AREA_MESSAGE);
        console.log('   → Clearing customer location');
        setCustomerLocation(null);
        console.log('   → Setting addressSelectedFromDropdown = FALSE');
        setAddressSelectedFromDropdown(false);
        console.log('   → Setting isServiceable = FALSE');
        // isServiceable already set to false above
      } else {
        // VALID - CRITICAL: Set BOTH states for button to show
        console.log('✅ Address IS serviceable!');
        console.log('   → Clearing addressError');
        setAddressError('');
        console.log('   → Setting isServiceable = TRUE');
        // isServiceable already set to true above  
        console.log('   → Setting addressSelectedFromDropdown = TRUE (BUTTON SHOULD NOW SHOW)');
        setAddressSelectedFromDropdown(true);
        
        // Map animation (background operation - doesn't affect button)
        const pendingLocation = (window as any).pendingMapAnimation;
        if (pendingLocation && mapRef.current) {
          console.log('   → Starting map animation (background)');
          setIsMapAnimating(true);
          setCustomerLocation(pendingLocation);
          
          // Animate in background
          mapRef.current.panTo(pendingLocation);
          setTimeout(() => {
            if (mapRef.current) {
              mapRef.current.setZoom(18);
            }
            setTimeout(() => {
              setIsMapAnimating(false);
            }, 700);
          }, 500);
          
          delete (window as any).pendingMapAnimation;
        }
        
        console.log('✅ STATE UPDATE COMPLETE - Button should be visible now!');
      }
      
      return result;
    } catch (err) {
      console.error('Error checking postal code availability:', err);
      setAddressError('Unable to check service availability');
      setIsServiceable(false);
      setAddressSelectedFromDropdown(false);
      return false;
    } finally {
      setIsAddressValidating(false);
    }
  };

  const handleNext = () => {
    console.log('🔘 NEXT button clicked!', {
      currentStep,
      isServiceable,
      address,
      postalCode,
      isLoading,
      isAddressValidating
    });
    
    switch (currentStep) {
      case 'location':
        if (!isServiceable) {
          console.log('❌ Address not serviceable, blocking navigation');
          setAddressError('Please enter a valid serviceable address');
          return;
        }
        console.log('✅ Moving to category step');
        setCurrentStep('category');
        break;
      case 'category':
        if (!selectedCategory) {
          setError('Please select a category');
          return;
        }
        setCurrentStep('service');
        break;
      case 'service':
        if (!selectedService) {
          setError('Please select a service');
          return;
        }
        setCurrentStep('datetime');
        break;
      case 'datetime':
        if (!selectedDate || !selectedTime) {
          setError('Please select both date and time');
          return;
        }
        setCurrentStep('customer');
        break;
      case 'customer':
        // Handle customer info submission
        break;
    }
    setError('');
  };

  const steps = useMemo(() => {
    const rest = [
      { step: 'category' as const, label: 'Category' },
      { step: 'service' as const, label: 'Service' },
      { step: 'datetime' as const, label: 'Date & Time' },
      { step: 'customer' as const, label: 'Info' },
    ];
    if (portalAssignedAssetId) return rest;
    return [{ step: 'location' as const, label: 'Location' }, ...rest];
  }, [portalAssignedAssetId]);

  const renderLocationStep = () => {
    const ctaReady = addressSelectedFromDropdown && isServiceable === true && !isAddressValidating && !isLoading;
    return (
      <div className="space-y-4">
        <div className="bg-white bg-opacity-95 backdrop-blur-md rounded-2xl shadow-2xl p-6">
          {/* Value-first headline: the address unlocks prices, it isn't a chore */}
          <div className="mb-4">
            <h2 className="text-[19px] font-semibold text-gray-900 leading-snug">
              {business?.name ? `Book ${business.name} online` : 'Book your service online'}
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              Enter your address to see services, pricing and availability in your area.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1">
              <input
                ref={inputRef}
                type="text"
                id="address"
                value={address}
                onChange={(e) => {
                  setAddress(e.target.value);
                  setAddressSelectedFromDropdown(false); // Reset when typing
                  setIsServiceable(null); // Clear validation
                }}
                placeholder="Street address"
                className="w-full h-12 px-4 rounded-[10px] border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-[15px] bg-white transition-all duration-200"
              />
            </div>
            <div className="w-full sm:w-20">
              <input
                type="text"
                id="unit"
                value={unitNumber}
                onChange={handleUnitNumberChange}
                placeholder="Unit"
                className="w-full h-12 px-4 rounded-[10px] border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-[15px] bg-white transition-all duration-200"
              />
            </div>
          </div>

          {/* Validation states */}
          {isAddressValidating && (
            <div className="mt-3 rounded-[10px] border border-blue-200 bg-blue-50 px-3 py-2.5">
              <div className="text-blue-700 text-[13px] font-medium flex items-center">
                <svg className="animate-spin mr-2.5 h-4 w-4 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Checking service availability...
              </div>
            </div>
          )}

          {/* Instant serviceability confirmation — answers "will they even come here?" */}
          {!isAddressValidating && isServiceable === true && addressSelectedFromDropdown && (
            <div className="mt-3 rounded-[10px] border border-green-200 bg-green-50 px-3 py-2.5">
              <p className="text-[13px] font-medium text-green-800">
                ✓ You're in {business?.name ? `${business.name}'s` : 'our'} service area
              </p>
            </div>
          )}

          {addressError && addressError === OUT_OF_SERVICE_AREA_MESSAGE ? (
            /* Out-of-area decline: soften the dead end with a direct call path */
            <div className="mt-3 rounded-[10px] border border-amber-200 bg-amber-50 px-3.5 py-3">
              <p className="text-[13px] font-medium text-amber-900">{OUT_OF_SERVICE_AREA_MESSAGE}</p>
              {business?.phone && (
                <a
                  href={`tel:${business.phone}`}
                  className="mt-2.5 flex items-center justify-center gap-2 w-full py-2.5 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-[14px] font-semibold transition-colors active:scale-[0.98] touch-manipulation"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  Call {business.phone}
                </a>
              )}
            </div>
          ) : addressError ? (
            <div className="mt-3 rounded-[10px] border border-red-200 bg-red-50 px-3 py-2.5">
              <p className="text-[13px] font-medium text-red-700">{addressError}</p>
            </div>
          ) : null}

          {/* CTA lives inside the card and promises the payoff, not "Next" */}
          <button
            onClick={handleNext}
            disabled={!ctaReady}
            className={`mt-4 w-full py-3.5 px-6 rounded-xl font-semibold text-[15px] transition-all duration-200 active:scale-[0.98] touch-manipulation ${
              ctaReady
                ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-lg'
                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
            }`}
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            {isAddressValidating
              ? 'Validating address...'
              : isLoading
                ? 'Checking availability...'
                : 'See services & prices'}
          </button>

        </div>
      </div>
    );
  };

  // Google reviews trust badge — persistent under every step except the intake
  // form (rendered as a floating pill since it sits over the map background).
  const renderGoogleReviewsBadge = () => {
    if (!googleReviewsBadge.enabled || googleReviewsBadge.rating == null) return null;
    const rating = Math.min(5, Math.max(0, googleReviewsBadge.rating));
    const fullStars = Math.round(rating);
    const badgeInner = (
      <span className="inline-flex items-center gap-1.5">
        <GoogleGIcon size={16} />
        <span className="text-amber-400 text-[15px] leading-none tracking-tight" aria-hidden="true">
          {'★'.repeat(fullStars)}
          <span className="text-gray-300">{'★'.repeat(5 - fullStars)}</span>
        </span>
        <span className="text-[13px] font-semibold text-gray-900">{rating}</span>
        {googleReviewsBadge.count != null && googleReviewsBadge.count > 0 && (
          <span className="text-[13px] text-gray-500">· based on {googleReviewsBadge.count}+ reviews</span>
        )}
      </span>
    );
    const pillClasses =
      'inline-flex items-center rounded-full px-3.5 py-2 bg-white bg-opacity-95 backdrop-blur-md shadow-lg';
    return (
      <div className="mt-3 flex justify-center">
        {googleReviewsBadge.url ? (
          <a
            href={googleReviewsBadge.url}
            target="_blank"
            rel="noopener noreferrer"
            className={`${pillClasses} hover:bg-gray-50 active:bg-gray-100 transition-colors touch-manipulation`}
            style={{ WebkitTapHighlightColor: 'transparent' }}
            aria-label={`Rated ${rating} out of 5 on Google — read reviews`}
          >
            {badgeInner}
          </a>
        ) : (
          <span className={pillClasses} aria-label={`Rated ${rating} out of 5 on Google`}>
            {badgeInner}
          </span>
        )}
      </div>
    );
  };

  const renderCategoryStep = () => {
    return (
      <div className="max-w-2xl mx-auto px-4 mt-8">
        <div className="bg-white bg-opacity-95 backdrop-blur-md rounded-2xl shadow-2xl p-6">
          {/* Header: back affordance + value-first headline (matches the location step) */}
          <div className="mb-4">
            <div className="flex items-center gap-2">
              {!portalAssignedAssetId ? (
                <button
                  onClick={() => setCurrentStep('location')}
                  className="p-1.5 -ml-1.5 text-gray-400 hover:text-gray-600 rounded-full transition-colors touch-manipulation"
                  aria-label="Go back"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
              ) : null}
              <h2 className="text-[19px] font-semibold text-gray-900 leading-snug">What are you looking for?</h2>
            </div>
            <p className="text-sm text-gray-500 mt-1">Pick a category to see services and prices.</p>
          </div>

          <div className="space-y-2.5">
            {isCategoriesLoading ? (
              <div className="py-8 text-center text-sm font-medium text-gray-500">Loading categories...</div>
            ) : categories.length === 0 && !error ? (
              <div className="py-8 text-center text-sm font-medium text-gray-500">No service categories available</div>
            ) : (
              categories.map((category) => {
                const isSel = selectedCategory === category.id;
                return (
                  <button
                    key={category.id}
                    onClick={() => {
                      // Tap-to-advance: selecting a category moves straight to
                      // services — one less tap than select-then-NEXT.
                      setSelectedCategory(category.id);
                      setError('');
                      setCurrentStep('service');
                    }}
                    className={`w-full flex items-center justify-between gap-3 rounded-xl border px-4 py-3.5 text-left transition-all duration-150 active:scale-[0.99] touch-manipulation ${
                      isSel
                        ? 'border-blue-500 bg-blue-50/60'
                        : 'border-gray-200 bg-white hover:border-blue-300 hover:bg-gray-50'
                    }`}
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    <span className="flex items-center gap-3 min-w-0">
                      {category.image_url && (
                        <img
                          src={category.image_url}
                          alt=""
                          loading="lazy"
                          className="h-12 w-12 rounded-lg object-cover flex-shrink-0 border border-gray-200/60"
                        />
                      )}
                      <span className={`text-[15px] font-semibold ${isSel ? 'text-blue-700' : 'text-gray-900'}`}>
                        {category.category_title}
                      </span>
                    </span>
                    <svg className={`w-4 h-4 flex-shrink-0 ${isSel ? 'text-blue-500' : 'text-gray-300'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                );
              })
            )}
          </div>

          {error && (
            <div className="mt-3 rounded-[10px] border border-red-200 bg-red-50 px-3 py-2.5">
              <p className="text-[13px] font-medium text-red-700">{error}</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderServiceStep = () => {
    const selectedCategoryData = categories.find(cat => cat.id === selectedCategory);
    // Show services immediately: default to the first sub-category so the list
    // is never hidden behind an unexplained empty state.
    const activeSubCategoryId = expandedSubCategory ?? selectedCategoryData?.sub_categories?.[0]?.id ?? null;

    // ── Inline add-ons phase: service picked, container swaps to add-on selection ──
    if (showAddonsModal && selectedService) {
      const pct = bookingDiscountPercentage;
      const svcBase = selectedService.priceOption.price;
      const addonsBase = selectedAddons.reduce((sum, a) => sum + (a.price || 0), 0);
      const netTotal = (svcBase + addonsBase) * (pct > 0 ? 1 - pct / 100 : 1);
      const svcNet = svcBase * (pct > 0 ? 1 - pct / 100 : 1);
      const fmt = (n: number) => (Number.isInteger(n) ? `$${n}` : `$${n.toFixed(2)}`);
      return (
        <div className="max-w-3xl mx-auto px-4 mt-8">
          <div className="bg-white bg-opacity-95 backdrop-blur-md rounded-2xl shadow-2xl p-6">
            {/* Header */}
            <div className="mb-4">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    setShowAddonsModal(false);
                    setSelectedAddons([]);
                  }}
                  className="p-1.5 -ml-1.5 text-gray-400 hover:text-gray-600 rounded-full transition-colors touch-manipulation"
                  aria-label="Back to services"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <h2 className="text-[19px] font-semibold text-gray-900 leading-snug">Customize your service</h2>
              </div>
              <p className="text-sm text-gray-500 mt-1">Add extras now — no extra trip charge.</p>
            </div>

            {/* Selected service summary */}
            <div className="rounded-xl border border-blue-500 bg-blue-50/60 px-4 py-3.5">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-blue-600">Selected</p>
                  <h4 className="text-[15px] font-semibold text-gray-900 leading-snug truncate">{selectedService.service.name}</h4>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <span className="text-[15px] font-semibold text-blue-600">{fmt(svcNet)}</span>
                  <button
                    onClick={() => {
                      setShowAddonsModal(false);
                      setSelectedAddons([]);
                    }}
                    className="text-[13px] font-medium text-gray-500 hover:text-gray-800 transition-colors touch-manipulation"
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    Change
                  </button>
                </div>
              </div>
            </div>

            {/* Available add-ons */}
            <p className="text-[13px] font-semibold text-gray-700 mt-4 mb-2">Available Add-Ons</p>
            <div className="space-y-2.5">
              {serviceAddons.map((addon) => {
                const isOn = !!selectedAddons.find(a => a.id === addon.id);
                const addonNet = (addon.price || 0) * (pct > 0 ? 1 - pct / 100 : 1);
                return (
                  <div
                    key={addon.id}
                    role="button"
                    tabIndex={0}
                    aria-pressed={isOn}
                    onClick={() => {
                      setSelectedAddons(prev =>
                        prev.find(a => a.id === addon.id)
                          ? prev.filter(a => a.id !== addon.id)
                          : [...prev, addon]
                      );
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setSelectedAddons(prev =>
                          prev.find(a => a.id === addon.id)
                            ? prev.filter(a => a.id !== addon.id)
                            : [...prev, addon]
                        );
                      }
                    }}
                    className={`rounded-xl border px-4 py-3.5 cursor-pointer transition-all duration-150 active:scale-[0.99] touch-manipulation ${
                      isOn ? 'border-blue-500 bg-blue-50/60' : 'border-gray-200 bg-white hover:border-blue-300'
                    }`}
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <h4 className="text-[15px] font-semibold text-gray-900 leading-snug">{addon.name}</h4>
                        {addon.description && (
                          <p className="text-[13px] text-gray-500 mt-0.5">{addon.description}</p>
                        )}
                        <div className="flex items-center gap-2 mt-1.5">
                          <span className="text-[14px] font-semibold text-blue-600">+{fmt(addonNet)}</span>
                          {addon.duration_minutes ? (
                            <span className="text-[12px] text-gray-500">{addon.duration_minutes} mins</span>
                          ) : null}
                        </div>
                      </div>
                      <div
                        className={`mt-0.5 w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                          isOn ? 'border-blue-600 bg-blue-600' : 'border-gray-300 bg-white'
                        }`}
                      >
                        {isOn && (
                          <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Price-carrying CTA: continue with or without add-ons. Sticky to
                the bottom of the step's scroll container so it stays in view
                while the add-on list scrolls — skipping is always one tap away */}
            <div className="sticky bottom-0 -mx-6 -mb-6 px-6 pb-5 pt-3 mt-4 bg-gradient-to-t from-white via-white/95 to-transparent rounded-b-2xl">
              <button
                onClick={() => handleAddonsSelection(selectedAddons)}
                className="w-full py-3.5 px-6 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold text-[15px] shadow-lg transition-all duration-200 active:scale-[0.98] touch-manipulation"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                {selectedAddons.length > 0
                  ? `Continue with ${selectedAddons.length} add-on${selectedAddons.length === 1 ? '' : 's'} — ${fmt(netTotal)}`
                  : `Continue without add-ons — ${fmt(netTotal)}`}
              </button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="max-w-3xl mx-auto px-4 mt-8">
        <div className="bg-white bg-opacity-95 backdrop-blur-md rounded-2xl shadow-2xl p-6">
          {/* Header: back affordance + value-first headline (matches earlier steps) */}
          <div className="mb-4">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentStep('category')}
                className="p-1.5 -ml-1.5 text-gray-400 hover:text-gray-600 rounded-full transition-colors touch-manipulation"
                aria-label="Go back"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <h2 className="text-[19px] font-semibold text-gray-900 leading-snug">Choose your service</h2>
            </div>
            <p className="text-sm text-gray-500 mt-1">Tap a service to continue — prices include any online discount.</p>
          </div>

          {!selectedCategoryData ? (
            <div className="py-8 text-center text-sm font-medium text-gray-500">Loading services...</div>
          ) : !selectedCategoryData?.sub_categories?.length ? (
            <div className="py-8 text-center text-sm font-medium text-gray-500">No services available in this category</div>
          ) : (
            <div>
              {/* Sub-category filter chips (only when there's a real choice) */}
              {selectedCategoryData.sub_categories.length > 1 && (
                <div className="flex flex-wrap gap-2 mb-4">
                  {selectedCategoryData.sub_categories.map((subCategory) => {
                    const isActive = activeSubCategoryId === subCategory.id;
                    return (
                      <button
                        key={subCategory.id}
                        onClick={() => setExpandedSubCategory(subCategory.id)}
                        className={`px-3.5 py-1.5 rounded-full text-[13px] font-semibold transition-all duration-150 active:scale-95 touch-manipulation ${
                          isActive
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                        style={{ WebkitTapHighlightColor: 'transparent' }}
                      >
                        {subCategory.title}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Services for the active sub-category */}
              {activeSubCategoryId && (() => {
                const activeServices =
                  selectedCategoryData.sub_categories.find(sub => sub.id === activeSubCategoryId)?.services ?? [];
                // When any card in this list has a badge, badge-less cards
                // reserve the same strip height so titles/prices stay aligned
                const anyServiceHasTag = activeServices.some((s) => getServiceTitleTag(s.service.id));
                return (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {activeServices.map((service, svcIndex) => {
                      const isSel =
                        selectedService?.service.id === service.service.id &&
                        selectedService?.priceOption.id === service.priceOption.id;
                      const titleTag = getServiceTitleTag(service.service.id);
                      const serviceMeter = getServiceMeter(service.service.id);
                      const isExpanded = expandedServiceIds.has(serviceCardKey(service));

                      const basePrice = service.priceOption.price;
                      const pct = bookingDiscountPercentage;
                      const net = pct > 0 ? basePrice * (1 - pct / 100) : basePrice;
                      const compareAt = (service.priceOption as any).compare_at_price;
                      const hasCompare = compareAt != null && Number(compareAt) > basePrice;
                      const struck = hasCompare ? Number(compareAt) : (pct > 0 ? basePrice : null);
                      const savingsPct = struck != null && struck > 0 ? Math.round(((struck - net) / struck) * 100) : 0;
                      const priceLabel = Number.isInteger(net) ? `$${net}` : `$${net.toFixed(2)}`;

                      return (
                        <div
                          key={`${service.service.id}-${service.priceOption.id}`}
                          role="button"
                          tabIndex={0}
                          aria-expanded={isExpanded}
                          onClick={() => toggleServiceRowExpanded(activeServices, svcIndex)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              toggleServiceRowExpanded(activeServices, svcIndex);
                            }
                          }}
                          className={`flex flex-col rounded-xl border px-4 py-3.5 cursor-pointer transition-all duration-150 active:scale-[0.99] touch-manipulation ${
                            isSel
                              ? 'border-blue-500 bg-blue-50/60'
                              : 'border-gray-200 bg-white hover:border-blue-300'
                          }`}
                          style={{ WebkitTapHighlightColor: 'transparent' }}
                        >
                          {/* Configured badge: a strip built into the top edge of the card
                              so it never crowds the service title. Cards without a badge
                              render an invisible strip of the same height whenever a
                              sibling card has one, keeping all cards perfectly in line. */}
                          {titleTag ? (
                            <div className="-mx-4 -mt-3.5 mb-2.5 px-3 py-1.5 rounded-t-[11px] bg-blue-600 text-white text-[11px] font-semibold text-center leading-none">
                              {titleTag.title_tag_text}
                            </div>
                          ) : anyServiceHasTag ? (
                            <div aria-hidden className="-mx-4 -mt-3.5 mb-2.5 px-3 py-1.5 text-[11px] font-semibold leading-none invisible">
                              &nbsp;
                            </div>
                          ) : null}

                          {/* Name. min-h reserves two title lines so the price rows of
                              side-by-side cards stay aligned. */}
                          <div className="min-h-[48px]">
                            <h4 className="text-[17px] font-semibold text-gray-900 leading-snug">{service.service.name}</h4>
                          </div>

                          {/* Price anchoring row: struck original + discounted price + duration */}
                          <div className="flex items-center gap-2 mt-2">
                            <span className="text-[15px] font-semibold text-blue-600">{priceLabel}</span>
                            {struck != null && (
                              <span className="text-[13px] text-red-500 line-through">${Number(struck).toLocaleString()}</span>
                            )}
                            <span className="flex-1" />
                            <span className="text-[12px] text-gray-500">{service.priceOption.duration_minutes} mins</span>
                          </div>

                          {/* Savings callout on its own line under the price */}
                          {savingsPct > 0 && (
                            <div className="mt-1 text-[12px] font-semibold text-red-500">
                              {savingsPct}% off online
                            </div>
                          )}

                          {/* Service meter (kept from the existing design) */}
                          {serviceMeter ? (
                            <div className="mt-2.5">
                              <p className="text-[12px] font-medium text-gray-600 mb-1">{serviceMeter.meter_title}</p>
                              <div className="w-full bg-gray-100 rounded-full h-1.5">
                                <div
                                  className="bg-blue-500 h-1.5 rounded-full transition-all duration-300"
                                  style={{ width: `${((serviceMeter.meter_rating || 0) / 10) * 100}%` }}
                                ></div>
                              </div>
                            </div>
                          ) : null}

                          {/* Expandable description */}
                          {isExpanded && (
                            <div className="mt-2.5 pt-2.5 border-t border-gray-200/60" onClick={(e) => e.stopPropagation()}>
                              {service.service.description ? (
                                <RichTextDescription
                                  description={service.service.description}
                                  className="text-[13px] text-gray-700 leading-relaxed"
                                />
                              ) : (
                                <p className="text-[13px] text-gray-500">No description available.</p>
                              )}
                            </div>
                          )}

                          {/* Footer: pinned to the card bottom (mt-auto) so Select buttons
                              stay aligned across cards even with different description lengths */}
                          <div className="flex items-center justify-between gap-3 mt-auto pt-3">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleServiceRowExpanded(activeServices, svcIndex);
                              }}
                              className="text-[13px] font-medium text-gray-500 hover:text-gray-800 transition-colors touch-manipulation"
                              style={{ WebkitTapHighlightColor: 'transparent' }}
                            >
                              {isExpanded ? 'Hide details' : 'Details'}
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleServiceSelection(service);
                              }}
                              className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-[13px] font-semibold transition-all duration-150 active:scale-[0.97] touch-manipulation"
                              style={{ WebkitTapHighlightColor: 'transparent' }}
                            >
                              Select — {priceLabel}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                </div>
                );
              })()}
            </div>
          )}

          {error && (
            <div className="mt-3 rounded-[10px] border border-red-200 bg-red-50 px-3 py-2.5">
              <p className="text-[13px] font-medium text-red-700">{error}</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Add function to fetch resource details
  const fetchResourceDetails = async (resourceIds: string[]) => {
    if (!supabase || resourceIds.length === 0) return;
    
    try {
      const { data, error } = await supabase
        .from('resources')
        .select('id, first_name, last_name, avatar_url')
        .in('id', resourceIds);
        
      if (error) {
        console.error('Error fetching resource details:', error);
        return;
      }
      
      const detailsMap: {[key: string]: {first_name: string; last_name: string; name?: string; avatar_url?: string}} = {};
      
      if (data && data.length > 0) {
        data.forEach(resource => {
          detailsMap[resource.id] = {
            first_name: resource.first_name,
            last_name: resource.last_name,
            name: `${resource.first_name} ${resource.last_name}`.trim(),
            avatar_url: resource.avatar_url || undefined
          };
        });
      }
      
      setResourceDetails(detailsMap);
      console.log('=== DIAGNOSTIC: Fetched resource details:', detailsMap);
    } catch (err) {
      console.error('Error in fetchResourceDetails:', err);
    }
  };

  const fetchAvailableResources = async () => {
    if (!supabase || !business?.id) {
      setIsLoadingResources(false);
      return;
    }

    const fixedAssetId = portalAssignedAssetId;

    if (fixedAssetId) {
      try {
        setIsLoadingResources(true);
        // Prefer mobile_asset_availability (any row — portal assignment overrides mobile_available flag).
        const { data: mobileRow, error: mobileErr } = await supabase
          .from('mobile_asset_availability')
          .select('id, asset_id, postal_codes, working_hours, mobile_available')
          .eq('business_id', business.id)
          .eq('asset_id', fixedAssetId)
          .maybeSingle();

        if (mobileErr) throw mobileErr;

        let resourceRowId: string;
        let postal_codes: ResourceAvailability['postal_codes'];
        let working_hours: ResourceAvailability['working_hours'];

        if (mobileRow) {
          resourceRowId = mobileRow.id;
          postal_codes = (mobileRow.postal_codes || {}) as ResourceAvailability['postal_codes'];
          working_hours = (mobileRow.working_hours || {}) as ResourceAvailability['working_hours'];
        } else {
          // Many assets only have asset_availability (no "mobile" toggle); use that for fixed-portal booking.
          const { data: aaRow, error: aaErr } = await supabase
            .from('asset_availability')
            .select('monday, tuesday, wednesday, thursday, friday, saturday, sunday')
            .eq('business_id', business.id)
            .eq('asset_id', fixedAssetId)
            .maybeSingle();

          if (aaErr) throw aaErr;

          if (!aaRow) {
            console.error('No availability rows for assigned portal asset:', fixedAssetId);
            toast.error(
              'This asset has no schedule for online booking yet. Turn on mobile booking for this asset, or set its availability in asset settings.'
            );
            setAvailableResources([]);
            setIsLoadingResources(false);
            return;
          }

          resourceRowId = `asset-availability:${fixedAssetId}`;
          postal_codes = {};
          working_hours = mapAssetAvailabilityRowToWorkingHours(aaRow as Record<string, unknown>);
        }

        let assetDetails: Record<string, { name?: string; asset_type?: string; image_url?: string }> = {};
        const { data: assetsInfo } = await supabase
          .from('assets')
          .select('id, name, asset_type, image_url')
          .eq('id', fixedAssetId)
          .maybeSingle();
        if (assetsInfo) {
          assetDetails[assetsInfo.id] = assetsInfo;
        }

        const details = assetDetails[fixedAssetId];
        const assetName = details?.name || 'Asset';
        const single: ResourceAvailability = {
          id: resourceRowId,
          resource_id: fixedAssetId,
          first_name: assetName,
          last_name: '',
          name: assetName,
          profile_photo: details?.image_url || undefined,
          postal_codes,
          working_hours,
          _isAsset: true,
          _assetType: details?.asset_type || undefined,
        };

        setAvailableResources([single]);
        setResourceDetails({});
        setIsLoadingResources(false);
        return;
      } catch (err) {
        console.error('Error fetching assigned asset availability:', err);
        toast.error('Unable to load availability for this booking page.');
        setAvailableResources([]);
        setIsLoadingResources(false);
        return;
      }
    }

    if (!postalCode) {
      setIsLoadingResources(false);
      return;
    }

    try {
      setIsLoadingResources(true);
      console.log('=== DIAGNOSTIC: Fetching available resources for postal code:', postalCode, 'and business:', business.id);
      
      // First get mobile availability records
      const { data, error } = await supabase
        .from('mobile_availability')
        .select('id, first_name, last_name, postal_codes, working_hours, mobile_available, resource_id')
        .eq('business_id', business.id)
        .eq('mobile_available', true);

      if (error) {
        console.error('Error fetching available resources:', error);
        throw error;
      }
      
      console.log('=== DIAGNOSTIC: Raw resource data from database:', data);
      console.log('=== DIAGNOSTIC: Number of resources found for this business:', data ? data.length : 0);

      let resourcesWithNames: ResourceAvailability[] = [];

      if (data && data.length > 0) {
        // Collect resource_ids to fetch detailed information
        const resourceIds = data
          .map(resource => resource.resource_id)
          .filter(id => id) as string[];
        
        // Fetch resource details if we have any valid resource_ids
        if (resourceIds.length > 0) {
          await fetchResourceDetails(resourceIds);
        }

        // Map the data to include all necessary fields
        resourcesWithNames = data.map(resource => {
          console.log('=== DIAGNOSTIC: Processing resource for business', business.id, ':', {
            id: resource.id,
            first_name: resource.first_name,
            last_name: resource.last_name,
            mobile_available: resource.mobile_available,
            resource_id: resource.resource_id || 'NOT_FOUND'
          });
          
          const details = resource.resource_id ? resourceDetails[resource.resource_id] : null;
          
          return {
            id: resource.id,
            resource_id: resource.resource_id,
            first_name: details?.first_name || resource.first_name,
            last_name: details?.last_name || resource.last_name,
            name: details?.name || `${resource.first_name} ${resource.last_name}`.trim(),
            profile_photo: details?.avatar_url,
            postal_codes: resource.postal_codes,
            working_hours: resource.working_hours
          };
        });
      }
      
      console.log('=== DIAGNOSTIC: Processed resources for business', business.id, ':', resourcesWithNames.length);

      // Also fetch available assets from mobile_asset_availability
      let assetEntries: ResourceAvailability[] = [];
      try {
        const { data: assetData, error: assetError } = await supabase
          .from('mobile_asset_availability')
          .select('id, asset_id, postal_codes, working_hours, mobile_available')
          .eq('business_id', business.id)
          .eq('mobile_available', true);

        if (!assetError && assetData && assetData.length > 0) {
          const assetIds = assetData.map(a => a.asset_id).filter(Boolean) as string[];
          
          let assetDetails: Record<string, any> = {};
          if (assetIds.length > 0) {
            const { data: assetsInfo } = await supabase
              .from('assets')
              .select('id, name, asset_type, image_url')
              .in('id', assetIds);
            if (assetsInfo) {
              assetsInfo.forEach(a => { assetDetails[a.id] = a; });
            }
          }

          assetEntries = assetData.map(record => {
            const details = record.asset_id ? assetDetails[record.asset_id] : null;
            const assetName = details?.name || 'Asset';
            return {
              id: record.id,
              resource_id: record.asset_id,
              first_name: assetName,
              last_name: '',
              name: assetName,
              profile_photo: details?.image_url || undefined,
              postal_codes: record.postal_codes || {},
              working_hours: record.working_hours || {},
              _isAsset: true,
              _assetType: details?.asset_type || undefined,
            };
          });
          console.log('📦 Available assets for business:', assetEntries.length);
        }
      } catch (err) {
        console.error('Error fetching available assets:', err);
      }

      setAvailableResources([...resourcesWithNames, ...assetEntries]);
      setIsLoadingResources(false);
    } catch (err) {
      console.error('Error fetching available resources:', err);
      setError('Unable to fetch available resources');
      setIsLoadingResources(false);
    }
  };

  useEffect(() => {
    if (currentStep === 'datetime' && (postalCode || portalAssignedAssetId)) {
      fetchAvailableResources();
    }
  }, [currentStep, postalCode, portalAssignedAssetId]);

  const getDayName = (date: Date): string => {
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    return days[date.getDay()];
  };

  const isPostalCodeAvailableForDay = (
    resource: ResourceAvailability,
    postalPrefix: string,
    dayName: string
  ): boolean => {
    const postalCodes = resource.postal_codes[dayName] || [];
    
    // Normalize the prefix we're looking for (first 3 chars, uppercase, trimmed)
    const normalizedPrefix = postalPrefix.toUpperCase().trim().substring(0, 3);
    
    // Check if any stored postal code matches (normalize stored codes too)
    const hasMatch = postalCodes.some(code => {
      if (!code) return false;
      
      // Normalize stored code: uppercase, trim, take first 3 chars only
      const normalizedStored = code.toString().toUpperCase().trim().substring(0, 3);
      return normalizedStored === normalizedPrefix;
    });
    
    return hasMatch;
  };

  // Helper function to merge overlapping time ranges
  const mergeTimeRanges = (ranges: Array<{ startMinutes: number; endMinutes: number }>): Array<{ startMinutes: number; endMinutes: number }> => {
    if (ranges.length === 0) return [];
    
    const sorted = [...ranges].sort((a, b) => a.startMinutes - b.startMinutes);
    const merged: Array<{ startMinutes: number; endMinutes: number }> = [sorted[0]];
    
    for (let i = 1; i < sorted.length; i++) {
      const current = sorted[i];
      const last = merged[merged.length - 1];
      
      if (current.startMinutes <= last.endMinutes) {
        last.endMinutes = Math.max(last.endMinutes, current.endMinutes);
      } else {
        merged.push(current);
      }
    }
    
    return merged;
  };

  // Helper function to convert time range to 30-minute slot strings
  const timeRangeToSlots = (range: { startMinutes: number; endMinutes: number }): string[] => {
    const slots: string[] = [];
    const startSlot = Math.floor(range.startMinutes / 30) * 30;
    const endSlot = Math.ceil(range.endMinutes / 30) * 30;
    
    for (let minutes = startSlot; minutes < endSlot; minutes += 30) {
      const hour = Math.floor(minutes / 60);
      const minute = minutes % 60;
      slots.push(`${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`);
    }
    
    return slots;
  };

  // Helper function to split multi-day booking into per-day entries
  const splitMultiDayBooking = (
    startDate: string,
    startTime: string,
    endDate: string,
    endTime: string
  ): Array<{ date: string; startMinutes: number; endMinutes: number }> => {
    const entries: Array<{ date: string; startMinutes: number; endMinutes: number }> = [];
    
    if (startDate === endDate) {
      // Single-day booking
      const [startHour, startMinute] = startTime.split(':').map(Number);
      const [endHour, endMinute] = endTime.split(':').map(Number);
      entries.push({
        date: startDate,
        startMinutes: startHour * 60 + startMinute,
        endMinutes: endHour * 60 + endMinute
      });
    } else {
      // Multi-day booking
      const start = new Date(startDate + 'T00:00:00');
      const end = new Date(endDate + 'T00:00:00');
      
      const [startHour, startMinute] = startTime.split(':').map(Number);
      const [endHour, endMinute] = endTime.split(':').map(Number);
      const startTimeMinutes = startHour * 60 + startMinute;
      const endTimeMinutes = endHour * 60 + endMinute;
      
      // First day
      entries.push({
        date: startDate,
        startMinutes: startTimeMinutes,
        endMinutes: 1440
      });
      
      // Middle days
      const current = new Date(start);
      current.setDate(current.setDate(current.getDate() + 1));
      
      while (current < end) {
        const dateStr = format(current, 'yyyy-MM-dd');
        entries.push({
          date: dateStr,
          startMinutes: 0,
          endMinutes: 1440
        });
        current.setDate(current.getDate() + 1);
      }
      
      // Last day
      entries.push({
        date: endDate,
        startMinutes: 0,
        endMinutes: endTimeMinutes
      });
    }
    
    return entries;
  };

  // Add this function to fetch all future bookings for the business
  const fetchExistingBookings = async () => {
    if (!supabase || !business?.id) return;

    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayDateStr = format(today, 'yyyy-MM-dd');

      console.log('🔍 PUBLIC BOOKING: Fetching bookings for business:', business.id, 'from date:', todayDateStr);
      
      // Fetch mobile bookings
      const { data: mobileBookings, error: mobileError } = await supabase
        .from('mobile_bookings')
        .select('id, resource_id, asset_id, booking_id, start_date, start_time, end_date, end_time, is_buffer_block')
        .eq('business_id', business.id)
        .gte('start_date', todayDateStr)
        .order('start_date', { ascending: true });

      if (mobileError) throw mobileError;

      console.log('📱 Mobile bookings:', mobileBookings?.length || 0);

      // Fetch ALL bookings (both mobile and location) so asset-only mobile bookings
      // are included in blocked slot calculation. Duplicate resource blocking is harmless.
      const { data: regularBookingsData, error: regularError } = await supabase
        .from('bookings')
        .select('id, start_date, start_time, end_date, end_time, status, mobile_booking, is_deleted')
        .eq('business_id', business.id)
        .neq('is_deleted', true);

      if (regularError) throw regularError;

      // Filter for future bookings
      const futureRegularBookings = (regularBookingsData || []).filter((b: any) => {
        const endDate = b.end_date || b.start_date;
        return endDate >= todayDateStr;
      });

      console.log('📋 Regular bookings:', futureRegularBookings.length);
          
      // Fetch resources and assets for regular bookings
      let regularBookings: any[] = [];
      if (futureRegularBookings.length > 0) {
        const bookingIds = futureRegularBookings.map(b => b.id);
        
        const { data: bookingResources, error: resourcesError } = await supabase
          .from('booking_resources')
          .select('booking_id, resource_id')
          .in('booking_id', bookingIds);

        // Also fetch booking_assets to block asset time slots
        let bookingAssets: any[] = [];
        try {
          const { data: baData, error: baError } = await supabase
            .from('booking_assets')
            .select('booking_id, asset_id')
            .in('booking_id', bookingIds);
          if (!baError && baData) bookingAssets = baData;
        } catch { /* non-critical */ }
        
        if (!resourcesError) {
          regularBookings = futureRegularBookings.map(booking => ({
            ...booking,
            resources: bookingResources?.filter((br: any) => br.booking_id === booking.id) || [],
            assets: bookingAssets.filter((ba: any) => ba.booking_id === booking.id) || []
          }));
        }
      }

      console.log('📦 Regular bookings with resources:', regularBookings.length);
      
      // Also fetch booking_assets for ALL future bookings (mobile and regular)
      // to block asset time slots (assets are NOT stored in mobile_bookings)
      let allBookingAssets: any[] = [];
      try {
        const allBookingIds = [
          ...(mobileBookings || []).map(b => b.id),
          ...futureRegularBookings.map(b => b.id)
        ];
        
        // Get booking_assets along with booking time data
        if (allBookingIds.length > 0) {
          // Fetch all mobile booking_ids first (mobile_bookings.booking_id → bookings.id)
          const mobileBookingBookingIds = [...new Set((mobileBookings || []).map(b => b.booking_id).filter(Boolean))];
          const regularBookingIds = futureRegularBookings.map(b => b.id);
          const uniqueBookingIds = [...new Set([...mobileBookingBookingIds, ...regularBookingIds])];

          if (uniqueBookingIds.length > 0) {
            const { data: baData, error: baError } = await supabase
              .from('booking_assets')
              .select('asset_id, start_date, end_date, booking_id')
              .in('booking_id', uniqueBookingIds);
            if (!baError && baData) allBookingAssets = baData;
          }
        }
      } catch { /* non-critical */ }

      console.log('📦 All booking_assets for blocking:', allBookingAssets.length);

      // Fetch calendar tasks (they block time for assigned resources/assets)
      let calendarTasks: any[] = [];
      try {
        const { data: tasksData, error: tasksError } = await supabase
          .from('calendar_tasks')
          .select('id, start_date, start_time, end_date, end_time, status, is_deleted')
          .eq('business_id', business.id)
          .eq('is_deleted', false)
          .neq('status', 'completed')
          .gte('end_date', todayDateStr);

        if (!tasksError && tasksData && tasksData.length > 0) {
          const taskIds = tasksData.map(t => t.id);

          const [taskResourcesRes, taskAssetsRes] = await Promise.all([
            supabase.from('calendar_task_resources').select('task_id, resource_id').in('task_id', taskIds),
            supabase.from('calendar_task_assets').select('task_id, asset_id').in('task_id', taskIds)
          ]);

          calendarTasks = tasksData.map(task => ({
            ...task,
            resources: taskResourcesRes.data?.filter((tr: any) => tr.task_id === task.id) || [],
            assets: taskAssetsRes.data?.filter((ta: any) => ta.task_id === task.id) || []
          }));

          console.log('📋 Calendar tasks for blocking:', calendarTasks.length);
        }
      } catch { /* non-critical */ }

      // Process into blocked time slots
      const blockedRangesByResource: Record<string, Record<string, Array<{ startMinutes: number; endMinutes: number }>>> = {};

      // Process mobile bookings (resources and asset buffer blocks)
      mobileBookings?.forEach(booking => {
        const { resource_id, asset_id, start_date, start_time, end_time } = booking;
        const ownerId = resource_id || asset_id;
        if (!ownerId) return;

        if (!blockedRangesByResource[ownerId]) {
          blockedRangesByResource[ownerId] = {};
        }
        if (!blockedRangesByResource[ownerId][start_date]) {
          blockedRangesByResource[ownerId][start_date] = [];
        }

        const [startHour, startMinute] = start_time.split(':').map(Number);
        const [endHour, endMinute] = end_time.split(':').map(Number);
        blockedRangesByResource[ownerId][start_date].push({
          startMinutes: startHour * 60 + startMinute,
          endMinutes: endHour * 60 + endMinute
        });
      });

      // Process booking_assets into blocked ranges (covers both mobile and regular bookings)
      allBookingAssets.forEach((ba: any) => {
        const assetId = ba.asset_id;
        if (!assetId || !ba.start_date || !ba.end_date) return;

        const startDt = new Date(ba.start_date);
        const endDt = new Date(ba.end_date);
        const dateStr = format(startDt, 'yyyy-MM-dd');

        if (!blockedRangesByResource[assetId]) {
          blockedRangesByResource[assetId] = {};
        }
        if (!blockedRangesByResource[assetId][dateStr]) {
          blockedRangesByResource[assetId][dateStr] = [];
        }

        blockedRangesByResource[assetId][dateStr].push({
          startMinutes: startDt.getHours() * 60 + startDt.getMinutes(),
          endMinutes: endDt.getHours() * 60 + endDt.getMinutes()
        });
      });

      // Process regular bookings
      regularBookings?.forEach(booking => {
        if (!booking.start_date || !booking.start_time) return;
        
        const endDate = booking.end_date || booking.start_date;
        const endTime = booking.end_time || booking.start_time;
        
        // Split booking into per-day entries
        const dayEntries = splitMultiDayBooking(
          booking.start_date,
          booking.start_time,
          endDate,
          endTime
        );

        // For each resource assigned to this booking
        const resources = booking.resources || [];
        resources.forEach((resourceAssignment: any) => {
          const resourceId = resourceAssignment.resource_id;
          if (!resourceId) return;

          if (!blockedRangesByResource[resourceId]) {
            blockedRangesByResource[resourceId] = {};
          }

          dayEntries.forEach(entry => {
            if (!blockedRangesByResource[resourceId][entry.date]) {
              blockedRangesByResource[resourceId][entry.date] = [];
            }
            blockedRangesByResource[resourceId][entry.date].push({
              startMinutes: entry.startMinutes,
              endMinutes: entry.endMinutes
            });
          });
        });

        // Also block time for assigned assets using the booking's day entries
        const assetAssignments = booking.assets || [];
        assetAssignments.forEach((ba: any) => {
          const assetId = ba.asset_id;
          if (!assetId) return;

          if (!blockedRangesByResource[assetId]) {
            blockedRangesByResource[assetId] = {};
          }

          dayEntries.forEach(entry => {
            if (!blockedRangesByResource[assetId][entry.date]) {
              blockedRangesByResource[assetId][entry.date] = [];
            }
            blockedRangesByResource[assetId][entry.date].push({
              startMinutes: entry.startMinutes,
              endMinutes: entry.endMinutes
            });
          });
        });
      });

      // Process calendar tasks into blocked time slots
      calendarTasks.forEach((task: any) => {
        if (!task.start_date || !task.start_time) return;

        const endDate = task.end_date || task.start_date;
        const endTime = task.end_time || task.start_time;

        const dayEntries = splitMultiDayBooking(
          task.start_date,
          task.start_time,
          endDate,
          endTime
        );

        // Block time for assigned resources
        (task.resources || []).forEach((tr: any) => {
          const resourceId = tr.resource_id;
          if (!resourceId) return;

          if (!blockedRangesByResource[resourceId]) {
            blockedRangesByResource[resourceId] = {};
          }

          dayEntries.forEach(entry => {
            if (!blockedRangesByResource[resourceId][entry.date]) {
              blockedRangesByResource[resourceId][entry.date] = [];
            }
            blockedRangesByResource[resourceId][entry.date].push({
              startMinutes: entry.startMinutes,
              endMinutes: entry.endMinutes
            });
          });
        });

        // Block time for assigned assets
        (task.assets || []).forEach((ta: any) => {
          const assetId = ta.asset_id;
          if (!assetId) return;

          if (!blockedRangesByResource[assetId]) {
            blockedRangesByResource[assetId] = {};
          }

          dayEntries.forEach(entry => {
            if (!blockedRangesByResource[assetId][entry.date]) {
              blockedRangesByResource[assetId][entry.date] = [];
            }
            blockedRangesByResource[assetId][entry.date].push({
              startMinutes: entry.startMinutes,
              endMinutes: entry.endMinutes
            });
          });
        });
      });

      // Merge overlapping ranges and convert to blocked slots
      const blocked: BlockedTimeSlots = {};

      Object.keys(blockedRangesByResource).forEach(resourceId => {
        blocked[resourceId] = [];
        
        Object.keys(blockedRangesByResource[resourceId]).forEach(date => {
          const ranges = blockedRangesByResource[resourceId][date];
          const mergedRanges = mergeTimeRanges(ranges);
          
          const allSlots: string[] = [];
          mergedRanges.forEach(range => {
            const slots = timeRangeToSlots(range);
            slots.forEach(slot => {
              if (!allSlots.includes(slot)) {
                allSlots.push(slot);
              }
            });
          });
          
          allSlots.sort();
          
          blocked[resourceId].push({
            date,
            blockedSlots: allSlots
          });
        });
        
        blocked[resourceId].sort((a, b) => a.date.localeCompare(b.date));
      });

      console.log('🔒 Total blocked slots created for', Object.keys(blocked).length, 'resources');
      setBlockedTimeSlots(blocked);
      setExistingBookings(mobileBookings || []);
    } catch (err) {
      console.error('Error fetching existing bookings:', err);
      toast.error('Failed to load existing bookings');
    }
  };

  // Add effect to fetch bookings when component mounts
  useEffect(() => {
    if (business?.id) {
      console.log('=== DIAGNOSTIC: Business ID available, fetching bookings');
      fetchExistingBookings();
    }
  }, [business?.id]);

  // Update isTimeSlotOverlapping function using the improved time comparison logic
  const isTimeSlotOverlapping = (
    mobileAvailabilityId: string,
    proposedStart: Date,
    proposedEnd: Date
  ): boolean => {
    console.log('=== DIAGNOSTIC: Checking overlap for mobile_availability ID:', {
      mobileAvailabilityId,
      proposedStart: proposedStart.toISOString(),
      proposedEnd: proposedEnd.toISOString(),
      proposedDate: proposedStart.toDateString()
    });

    // Find the resource in our available resources list to get the mapped resource_id
    const resource = availableResources.find(r => r.id === mobileAvailabilityId);
    if (!resource) {
      console.log('=== DIAGNOSTIC: Resource not found in available resources!');
      return false;
    }
    
    // Get the actual resource_id used in the bookings table
    const bookingResourceId = resource.resource_id;
    
    if (!bookingResourceId) {
      console.log('=== DIAGNOSTIC: No resource_id mapping found for this resource, cannot check for overlaps');
      return false;
    }
    
    // Check using the blocked time slots approach
    const localDate = new Date(proposedStart);
    const dateStr = format(localDate, 'yyyy-MM-dd');
    
    console.log('=== DIAGNOSTIC: Using resource_id for booking lookup:', bookingResourceId, 'date:', dateStr);

    // If we don't have blocked slots for this resource_id, it's available
    if (!blockedTimeSlots[bookingResourceId]) {
      console.log('=== DIAGNOSTIC: No blocked slots for this resource, slot is available');
      return false;
    }

    // Look for blocked slots for this date
    const dateEntry = blockedTimeSlots[bookingResourceId].find((entry: BlockedTimeSlot) => entry.date === dateStr);
    if (!dateEntry) {
      console.log('=== DIAGNOSTIC: No blocked slots for this date, slot is available');
      return false;
    }

    // Calculate total duration for the proposed booking (service + add-ons)
    const serviceDuration = selectedService?.priceOption.duration_minutes || 0;
    const addonDuration = selectedAddons.reduce((sum, addon) => sum + (addon.duration_minutes || 0), 0);
    const fullServiceDuration = serviceDuration + addonDuration;

    // Convert booking start time to minutes since midnight
    const bookingStartMinutes = proposedStart.getHours() * 60 + proposedStart.getMinutes();
    const bookingEndMinutes = bookingStartMinutes + fullServiceDuration;

    // Convert blocked slots to minute ranges
    const blockedRanges = dateEntry.blockedSlots.map((slot: string) => {
      const [hour, minute] = slot.split(':').map(Number);
      const slotMinutes = hour * 60 + minute;
      return {
        start: slotMinutes,
        end: slotMinutes + 30 // Each slot is 30 minutes
      };
    });

    // Check if booking overlaps with any blocked range
    const hasOverlap = blockedRanges.some((range: { start: number; end: number }) => {
      // Booking starts before blocked slot ends AND booking ends after blocked slot starts
      const overlaps = (bookingStartMinutes < range.end && bookingEndMinutes > range.start);
      if (overlaps) {
        console.log('=== DIAGNOSTIC: Found overlap with blocked slot:', range);
      }
      return overlaps;
    });

    if (hasOverlap) {
      console.log('=== DIAGNOSTIC: Slot overlaps with existing booking, not available');
    } else {
      console.log('=== DIAGNOSTIC: No overlap found, slot is available');
    }

    return hasOverlap;
  };

  // Modify the getAvailableTimeSlots function to use more precise logging
  const getAvailableTimeSlots = async (date: Date) => {
    const assetOnly = !!portalAssignedAssetId;
    if ((!postalCode && !assetOnly) || !selectedService || !supabase) return [];

    console.log('=== DIAGNOSTIC: Generating time slots for date:', date.toLocaleDateString(), date.toISOString());
    console.log('=== DIAGNOSTIC: Existing bookings count:', existingBookings.length);
    console.log('=== DIAGNOSTIC: Buffer time:', bufferTime, 'minutes');

    const dayName = getDayName(date);
    const postalPrefix = postalCode ? postalCode.substring(0, 3).toUpperCase() : '';
    const slotMap = new Map<string, TimeSlot>();
    const serviceDuration = selectedService.priceOption.duration_minutes;
    const addonDuration = selectedAddons.reduce((sum, addon) => sum + (addon.duration_minutes || 0), 0);
    const fullServiceDuration = serviceDuration + addonDuration;
    // Calculate total duration including buffer time
    const totalDuration = fullServiceDuration + bufferTime;
    console.log('=== DIAGNOSTIC: Total duration (service + addons + buffer):', totalDuration, 'minutes (service:', serviceDuration, '+ addons:', addonDuration, '+ buffer:', bufferTime, ')');
    const now = new Date();

    try {
      // Find resources that serve this postal code on this day (asset-only portals skip postal matching)
      const availableResourcesForDay = availableResources.filter(resource => {
        const daySchedule = resource.working_hours[dayName];
        const postalOk =
          assetOnly &&
          resource._isAsset &&
          portalAssignedAssetId &&
          resource.resource_id === portalAssignedAssetId
            ? true
            : isPostalCodeAvailableForDay(resource, postalPrefix, dayName);
        return postalOk && daySchedule?.enabled && daySchedule?.shifts?.length > 0;
      });

      console.log('=== DIAGNOSTIC: Available resources for day:', availableResourcesForDay.map(r => ({
        id: r.id, // mobile_availability.id
        resource_id: r.resource_id, // This is the critical ID used in bookings
        name: r.name,
        shifts: r.working_hours[dayName]?.shifts
      })));

      if (availableResourcesForDay.length === 0) return [];

      // Format date for blocked slot lookup
      const dateStr = format(date, 'yyyy-MM-dd');

      // Generate time slots for each resource
      availableResourcesForDay.forEach(resource => {
        // Skip resources with no resource_id mapping
        if (!resource.resource_id) {
          console.log(`=== DIAGNOSTIC: Skipping resource ${resource.id} (${resource.name}) with no resource_id mapping`);
          return;
        }
        
        const daySchedule = resource.working_hours[dayName];

        daySchedule.shifts.forEach(shift => {
          const [startHour, startMinute] = shift.start.split(':').map(Number);
          const [endHour, endMinute] = shift.end.split(':').map(Number);

          const shiftStart = new Date(date);
          shiftStart.setHours(startHour, startMinute, 0, 0);

          const shiftEnd = new Date(date);
          shiftEnd.setHours(endHour, endMinute, 0, 0);

          console.log('=== DIAGNOSTIC: Processing shift for resource:', {
            id: resource.id, // mobile_availability.id (for reference only)
            resource_id: resource.resource_id, // The ID used for booking lookups
            resourceName: resource.name,
            shiftStart: shiftStart.toISOString(),
            shiftEnd: shiftEnd.toISOString(),
            serviceDuration,
            totalDuration
          });

          // Get blocked slots for this resource
          const resourceBlockedSlots = blockedTimeSlots[resource.resource_id || ''];
          const dateBlockedSlots = resourceBlockedSlots
            ? resourceBlockedSlots.find((entry: BlockedTimeSlot) => entry.date === dateStr)?.blockedSlots || []
            : [];
            
          console.log('=== DIAGNOSTIC: Blocked slots for this resource/date:', dateBlockedSlots);

          // Convert blocked slots to minute ranges for easier comparison
          const blockedRanges = dateBlockedSlots.map((slot: string) => {
            const [hour, minute] = slot.split(':').map(Number);
            const slotMinutes = hour * 60 + minute;
            return {
              start: slotMinutes,
              end: slotMinutes + 30 // Each slot is 30 minutes
            };
          });

          // Generate 30-minute slots
          const current = new Date(shiftStart);
          while (current <= shiftEnd) {
            // Skip slots in the past
            if (current <= now) {
              current.setMinutes(current.getMinutes() + 30);
              continue;
            }

            // Calculate service end time (including buffer)
            const serviceEnd = new Date(current);
            // Use total duration (service + buffer) for end time calculation
            serviceEnd.setMinutes(serviceEnd.getMinutes() + totalDuration);

            // Skip if service would end after shift end
            if (serviceEnd > shiftEnd) {
              break;
            }

            // Convert current time to minutes since midnight
            const slotStartMinutes = current.getHours() * 60 + current.getMinutes();
            // Use total duration (service + buffer) for end time calculation
            const slotEndMinutes = slotStartMinutes + totalDuration;

            console.log('=== DIAGNOSTIC: Checking availability for potential slot:', {
              start: current.toISOString(),
              end: serviceEnd.toISOString(),
              resourceId: resource.id,
              resource_id: resource.resource_id,
              slotStartMinutes,
              slotEndMinutes,
              includesBuffer: bufferTime > 0
            });

            // Check if this slot overlaps with any blocked range
            const hasOverlap = blockedRanges.some((range: { start: number; end: number }) => {
              // Slot starts before blocked range ends AND slot ends after blocked range starts
              return (slotStartMinutes < range.end && slotEndMinutes > range.start);
            });

            const isAvailable = !hasOverlap;
            console.log('=== DIAGNOSTIC: Slot available?', isAvailable);

            if (isAvailable) {
              const timeKey = current.toISOString();
              const existingSlot = slotMap.get(timeKey) || {
                time: new Date(current),
                availableResources: []
              };

              existingSlot.availableResources.push({
                id: resource.id,
                resource_id: resource.resource_id,
                first_name: resource.first_name,
                last_name: resource.last_name,
                name: resource.name,
                _isAsset: resource._isAsset,
                _assetType: resource._assetType,
              });

              slotMap.set(timeKey, existingSlot);
              console.log('=== DIAGNOSTIC: Added available slot:', {
                time: current.toISOString(),
                resource: resource.name,
                resource_id: resource.resource_id
              });
            }

            current.setMinutes(current.getMinutes() + 30);
          }
        });
      });

      const timeSlots = Array.from(slotMap.values())
        .sort((a, b) => a.time.getTime() - b.time.getTime());

      console.log('=== DIAGNOSTIC: Generated available time slots:', timeSlots.length);
      console.log('=== DIAGNOSTIC: First few time slots:', timeSlots.slice(0, 3).map(slot => ({
        time: slot.time.toISOString(),
        resourcesCount: slot.availableResources.length,
        resources: slot.availableResources.map(r => ({ 
          id: r.id, 
          resource_id: r.resource_id,
          name: r.name 
        }))
      })));
      
      return timeSlots;
    } catch (err) {
      console.error('=== DIAGNOSTIC: Error generating time slots:', err);
      return [];
    }
  };

  const handleDateSelect = async (date: Date | null) => {
    setSelectedDate(date);
    setSelectedTime(null);
    if (date) {
      console.log('=== DIAGNOSTIC: Date selected:', date.toISOString(), 'Fetching time slots...');
      const slots = await getAvailableTimeSlots(date);
      console.log('=== DIAGNOSTIC: Time slots fetched:', slots.length);
      setAvailableTimeSlots(slots);
    } else {
      setAvailableTimeSlots([]);
    }
  };

  const handleTimeSlotClick = (timeSlot: TimeSlot) => {
    console.log('=== DIAGNOSTIC: Time slot clicked:', {
      time: timeSlot.time,
      resources: timeSlot.availableResources.map(r => ({
        id: r.id,
        resource_id: r.resource_id, // Log resource_id for debugging
        first_name: r.first_name,
        last_name: r.last_name,
        name: r.name
      }))
    });

    // Separate resources and assets
    const slotResources = timeSlot.availableResources.filter(r => !r._isAsset);
    const slotAssets = timeSlot.availableResources.filter(r => r._isAsset);

    if (slotResources.length + slotAssets.length === 1) {
      // Auto-select the single available resource or asset
      const selected = timeSlot.availableResources[0];
      setSelectedTime(timeSlot.time);
      if (selected._isAsset) {
        setSelectedAssetId(selected.resource_id || selected.id);
        setSelectedResourceId(null);
      } else {
        setSelectedResourceId(selected.resource_id || selected.id);
        setSelectedAssetId(null);
      }
      setSelectedTimeForModal(timeSlot);
    } else if (slotResources.length === 1 && slotAssets.length === 0) {
      setSelectedTime(timeSlot.time);
      setSelectedResourceId(slotResources[0].resource_id || slotResources[0].id);
      setSelectedAssetId(null);
      setSelectedTimeForModal(timeSlot);
    } else if (slotResources.length === 0 && slotAssets.length === 1) {
      setSelectedTime(timeSlot.time);
      setSelectedAssetId(slotAssets[0].resource_id || slotAssets[0].id);
      setSelectedResourceId(null);
      setSelectedTimeForModal(timeSlot);
    } else {
      // Multiple options — show selection modal
      setSelectedTimeForModal(timeSlot);
      setIsResourceModalOpen(true);
    }
  };

  const handleResourceSelect = (entityId: string, isAsset?: boolean) => {
    if (selectedTimeForModal) {
      setSelectedTime(selectedTimeForModal.time);
      if (isAsset) {
        setSelectedAssetId(entityId);
        setSelectedResourceId(null);
      } else {
        setSelectedResourceId(entityId);
        setSelectedAssetId(null);
      }
      setIsResourceModalOpen(false);
      setSelectedTimeForModal(null);
    }
  };

  const ResourceSelectionModal = () => {
    if (!selectedTimeForModal) return null;

    const slotResources = selectedTimeForModal.availableResources.filter(r => !r._isAsset);
    const slotAssets = selectedTimeForModal.availableResources.filter(r => r._isAsset);

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
          <h3 className="text-lg font-semibold mb-4">Select Available Provider</h3>
          <p className="text-sm text-gray-600 mb-4">
            {selectedTimeForModal.time.toLocaleTimeString('en-US', {
              hour: 'numeric',
              minute: '2-digit',
              hour12: true
            })}
          </p>
          <div className="space-y-2">
            {/* Team members */}
            {slotResources.length > 0 && slotAssets.length > 0 && (
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide px-1 pt-1">Team</p>
            )}
            {slotResources.map(resource => {
              const details = resource.resource_id ? resourceDetails[resource.resource_id] : null;
              const firstName = details?.first_name || resource.first_name;
              const lastName = details?.last_name || resource.last_name;
              const profilePhoto = details?.avatar_url || resource.profile_photo;
              const initials = getInitials(firstName, lastName);
              const idToUse = resource.resource_id || resource.id;
              return (
                <button
                  key={resource.id}
                  onClick={() => handleResourceSelect(idToUse, false)}
                  className="w-full py-3 px-4 text-left hover:bg-gray-50 rounded-md transition-colors flex items-center space-x-3"
                >
                  <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-medium overflow-hidden">
                    {profilePhoto ? (
                      <img src={profilePhoto} alt={initials} className="h-full w-full object-cover" />
                    ) : (
                      initials
                    )}
                  </div>
                  <span>
                    {details?.name || resource.name || 
                     `${firstName || ''} ${lastName || ''}`.trim() || 
                     `Resource ${resource.id.slice(0, 4)}`}
                  </span>
                </button>
              );
            })}
            {/* Assets */}
            {slotAssets.length > 0 && slotResources.length > 0 && (
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide px-1 pt-2">Assets</p>
            )}
            {slotAssets.map(asset => {
              const idToUse = asset.resource_id || asset.id;
              const assetName = asset.name || asset.first_name || 'Asset';
              const assetInitials = assetName.split(/\s+/).map((w: string) => w[0]).join('').toUpperCase().slice(0, 2);
              return (
                <button
                  key={asset.id}
                  onClick={() => handleResourceSelect(idToUse, true)}
                  className="w-full py-3 px-4 text-left hover:bg-gray-50 rounded-md transition-colors flex items-center space-x-3"
                >
                  <div className="w-8 h-8 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center font-medium text-xs">
                    {assetInitials}
                  </div>
                  <div>
                    <span>{assetName}</span>
                    {asset._assetType && (
                      <span className="ml-2 text-xs text-gray-400 capitalize">{asset._assetType}</span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
          <button
            onClick={() => setIsResourceModalOpen(false)}
            className="mt-4 w-full py-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  };

  // ⚡ PERFORMANCE OPTIMIZATION: Memoized date availability checker with caching
  const isDateAvailable = useCallback((date: Date): boolean => {
    if ((!postalCode && !portalAssignedAssetId) || !selectedService) return false;
    
    // Create cache key based on date + postal code (or fixed asset) + service
    const cacheKey = `${date.toDateString()}-${portalAssignedAssetId || postalCode}-${selectedService.service.id}`;
    
    // Check cache first
    if (dateAvailabilityCache.current.has(cacheKey)) {
      return dateAvailabilityCache.current.get(cacheKey)!;
    }
    
    // Check if date is in the past (before today)
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);
    if (checkDate < today) {
      dateAvailabilityCache.current.set(cacheKey, false);
      return false;
    }

    const dayName = getDayName(date);
    const postalPrefix = postalCode ? postalCode.substring(0, 3).toUpperCase() : '';
    const serviceDuration = selectedService.priceOption.duration_minutes;
    const addonDuration = selectedAddons.reduce((sum, addon) => sum + (addon.duration_minutes || 0), 0);
    // Match getAvailableTimeSlots: buffer time counts toward the required window
    const totalDuration = serviceDuration + addonDuration + bufferTime;
    const now = new Date();
    const dateStr = format(date, 'yyyy-MM-dd');

    // Check if any resource has at least one REAL bookable slot that day —
    // schedule/postal checks plus existing bookings (blockedTimeSlots), so
    // fully-booked days are greyed out instead of getting a dot.
    const availableResourcesForDay = availableResources.filter(resource => {
      const daySchedule = resource.working_hours[dayName];
      const postalOk =
        portalAssignedAssetId &&
        resource._isAsset &&
        resource.resource_id === portalAssignedAssetId
          ? true
          : isPostalCodeAvailableForDay(resource, postalPrefix, dayName);
      const isAvailable =
        postalOk && daySchedule?.enabled && daySchedule?.shifts?.length > 0;

      if (!isAvailable) return false;

      // Blocked (already booked) 30-min slots for this resource on this date
      const resourceBlockedSlots = resource.resource_id ? blockedTimeSlots[resource.resource_id] : undefined;
      const dateBlockedSlots = resourceBlockedSlots
        ? resourceBlockedSlots.find((entry: BlockedTimeSlot) => entry.date === dateStr)?.blockedSlots || []
        : [];
      const blockedRanges = dateBlockedSlots.map((slot: string) => {
        const [hour, minute] = slot.split(':').map(Number);
        const slotMinutes = hour * 60 + minute;
        return { start: slotMinutes, end: slotMinutes + 30 };
      });

      // For each shift, walk the 30-min grid and stop at the first slot that is
      // in the future, fits the full duration, and doesn't hit a blocked range
      // (mirrors getAvailableTimeSlots).
      return daySchedule.shifts.some(shift => {
        const [startHour, startMinute] = shift.start.split(':').map(Number);
        const [endHour, endMinute] = shift.end.split(':').map(Number);

        const shiftStart = new Date(date);
        shiftStart.setHours(startHour, startMinute, 0, 0);
        const shiftEnd = new Date(date);
        shiftEnd.setHours(endHour, endMinute, 0, 0);

        const current = new Date(shiftStart);
        while (current <= shiftEnd) {
          if (current <= now) {
            current.setMinutes(current.getMinutes() + 30);
            continue;
          }
          const serviceEnd = new Date(current);
          serviceEnd.setMinutes(serviceEnd.getMinutes() + totalDuration);
          if (serviceEnd > shiftEnd) break;

          const slotStartMinutes = current.getHours() * 60 + current.getMinutes();
          const slotEndMinutes = slotStartMinutes + totalDuration;
          const hasOverlap = blockedRanges.some((range: { start: number; end: number }) =>
            slotStartMinutes < range.end && slotEndMinutes > range.start
          );
          if (!hasOverlap) return true;

          current.setMinutes(current.getMinutes() + 30);
        }
        return false;
      });
    });

    const result = availableResourcesForDay.length > 0;
    
    // Cache the result
    dateAvailabilityCache.current.set(cacheKey, result);
    
    return result;
  }, [postalCode, portalAssignedAssetId, selectedService, availableResources, blockedTimeSlots, bufferTime, selectedAddons]);

  // ⚡ PERFORMANCE OPTIMIZATION: Memoized day renderer
  const renderDayContents = useCallback((day: number, date: Date) => {
    const hasTimeSlots = isDateAvailable(date);
    
    return (
      <div className="relative flex flex-col items-center">
        <div>{day}</div>
        {hasTimeSlots && (
          <div className="absolute -bottom-2 w-1.5 h-1.5 rounded-full bg-blue-500"></div>
        )}
      </div>
    );
  }, [isDateAvailable]);

  // Clear cache when postal code, fixed asset, service, add-ons, bookings, or buffer change
  // (the cache key doesn't encode blocked slots / buffer, so stale entries must go)
  useEffect(() => {
    dateAvailabilityCache.current.clear();
  }, [postalCode, portalAssignedAssetId, selectedService?.service.id, selectedAddons, blockedTimeSlots, bufferTime]);

  // Default the calendar to the first available day so visitors land on real
  // times instead of an unselected calendar. Only when nothing is selected yet —
  // never overrides a date the visitor picked themselves.
  useEffect(() => {
    if (currentStep !== 'datetime' || isLoadingResources || !selectedService || selectedDate) return;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    for (let i = 0; i < 60; i++) {
      const candidate = new Date(today);
      candidate.setDate(candidate.getDate() + i);
      if (isDateAvailable(candidate)) {
        void handleDateSelect(candidate);
        break;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, isLoadingResources, selectedService?.service.id, selectedDate, isDateAvailable]);

  const renderDateTimeStep = () => {
    const today = new Date();

    return (
      <div className="max-w-2xl mx-auto px-4 pb-32">
        <div className="bg-white bg-opacity-95 backdrop-blur-md rounded-2xl shadow-2xl p-6">
          {/* Header: back affordance + value-first headline (matches earlier steps) */}
          <div className="mb-4">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentStep('service')}
                className="p-1.5 -ml-1.5 text-gray-400 hover:text-gray-600 rounded-full transition-colors touch-manipulation"
                aria-label="Go back"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <h2 className="text-[19px] font-semibold text-gray-900 leading-snug">Pick a date & time</h2>
            </div>
            <p className="text-sm text-gray-500 mt-1">Days with a dot have open times.</p>
          </div>

          <div className="space-y-4 sm:space-y-6">
            <style>
          {`
            .react-datepicker__navigation {
              background: none;
              line-height: 1.7rem;
              text-align: center;
              cursor: pointer;
              position: absolute;
              top: 2px;
              width: 0;
              padding: 0;
              border: 0.45rem solid transparent;
              z-index: 1;
              height: 0.45rem;
              width: 0.45rem;
              text-indent: -999em;
              overflow: hidden;
            }
            .react-datepicker__navigation--previous {
              left: 2px;
              border-right-color: #ccc;
            }
            .react-datepicker__navigation--next {
              right: 2px;
              border-left-color: #ccc;
            }
            .react-datepicker__navigation:hover *::before {
              border-color: #a6a6a6;
            }
            .react-datepicker__navigation-icon {
              position: relative;
              top: -1px;
              font-size: 20px;
              width: 0;
            }
            .react-datepicker__navigation-icon--previous {
              left: 2px;
            }
            .react-datepicker__navigation-icon--next {
              left: -2px;
            }
            .react-datepicker__navigation-icon--previous:before {
              content: "";
              border-color: #ccc;
              border-style: solid;
              border-width: 3px 3px 0 0;
              display: block;
              height: 9px;
              width: 9px;
              position: absolute;
              top: 6px;
              transform: rotate(225deg);
              right: 0px;
            }
            .react-datepicker__navigation-icon--next:before {
              content: "";
              border-color: #ccc;
              border-style: solid;
              border-width: 3px 3px 0 0;
              display: block;
              height: 9px;
              width: 9px;
              position: absolute;
              top: 6px;
              transform: rotate(45deg);
              left: 0px;
            }
          `}
        </style>
        <div>
          {isLoadingResources ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
                <p className="text-gray-600 font-medium">Loading availability...</p>
              </div>
            </div>
          ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                Select Date
              </label>
              <DatePicker
                key={`datepicker-${calendarRefreshKey}`}
                selected={selectedDate}
                onChange={handleDateSelect}
                minDate={today}
                inline
                forceShowMonthNavigation
                showDisabledMonthNavigation
                calendarClassName="border border-gray-200 rounded-lg"
                filterDate={isDateAvailable}
                renderDayContents={renderDayContents}
                dayClassName={() => "relative py-1 sm:py-2"}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                Available Time Slots
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 sm:gap-2 border border-gray-200 rounded-lg p-2 sm:p-4">
                {availableTimeSlots.length > 0 ? (
                  availableTimeSlots.map((slot, index) => (
                    <button
                      key={index}
                      onClick={() => handleTimeSlotClick(slot)}
                      className={`
                        relative py-1.5 sm:py-2 px-2 sm:px-3 rounded-md text-xs sm:text-sm font-medium transition-colors
                        ${slot.time === selectedTime
                          ? 'bg-blue-500 text-white'
                          : 'bg-gray-50 text-gray-900 hover:bg-gray-100'
                        }
                      `}
                    >
                      <div>
                        {slot.time.toLocaleTimeString('en-US', { 
                          hour: 'numeric',
                          minute: '2-digit',
                          hour12: true 
                        })}
                      </div>
                      {slot.availableResources.length > 1 && (
                        <div className="text-xs mt-0.5">
                          {slot.availableResources.length} available
                        </div>
                      )}
                    </button>
                  ))
                ) : (
                  <p className="col-span-2 sm:col-span-3 text-center text-gray-500 text-xs sm:text-sm py-4">
                    {selectedDate ? 'No available time slots for this date' : 'Select a date to see available times'}
                  </p>
                )}
              </div>
            </div>
          </div>
          )}
        </div>
        
        {/* In-card CTA: below the calendar picker, above the service summary */}
        {!isLoadingResources && (
          <button
            onClick={handleNext}
            disabled={!selectedDate || !selectedTime}
            className={`w-full py-3.5 px-6 rounded-xl font-semibold text-[15px] transition-all duration-200 active:scale-[0.98] touch-manipulation ${
              selectedDate && selectedTime
                ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-lg'
                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
            }`}
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            {selectedTime ? 'Continue' : 'Select a time to continue'}
          </button>
        )}

        {/* Add service summary section */}
        {selectedService && currentStep !== 'customer' && !isLoadingResources && (
          <div className="p-3 sm:p-4 bg-gray-50 rounded-lg text-sm">
            <h3 className="font-semibold mb-2">Service Summary</h3>
            <div className="space-y-1 sm:space-y-2">
              <div>
                <span className="font-medium">Service: </span>
                {selectedService.service.name}
              </div>
              <div>
                <span className="font-medium">Price: </span>
                ${(selectedService.priceOption.price + selectedAddons.reduce((total, addon) => total + (addon.price || 0), 0)).toFixed(2)}
              </div>
              
              {/* Show selected add-ons */}
              {selectedAddons.length > 0 && (
                <div>
                  <span className="font-medium">Add-ons: </span>
                  <div className="ml-4 space-y-1">
                    {selectedAddons.map(addon => (
                      <div key={addon.id} className="text-sm">
                        • {addon.name} - ${addon.price.toFixed(2)}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <div>
                <span className="font-medium">Duration: </span>
                {selectedService.priceOption.duration_minutes + selectedAddons.reduce((total, addon) => total + (addon.duration_minutes || 0), 0)} minutes
              </div>
              
              {selectedTime && (selectedResourceId || selectedAssetId) ? (
                <>
                  <div className="pt-1 sm:pt-2 border-t mt-1 sm:mt-2">
                    <span className="font-medium">Date: </span>
                    {new Date(selectedTime).toLocaleDateString('en-US', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long', 
                      day: 'numeric'
                    })}
                  </div>
                  
                  <div>
                    <span className="font-medium">Time: </span>
                    {(() => {
                      const startTime = new Date(selectedTime);
                      const totalDur = selectedService.priceOption.duration_minutes + selectedAddons.reduce((sum: number, a: any) => sum + (a.duration_minutes || 0), 0);
                      const endTime = new Date(startTime.getTime() + totalDur * 60000);
                      
                      return (
                        <>
                          {startTime.toLocaleTimeString('en-US', { 
                            hour: 'numeric',
                            minute: '2-digit',
                            hour12: true 
                          })} - {endTime.toLocaleTimeString('en-US', { 
                            hour: 'numeric',
                            minute: '2-digit',
                            hour12: true 
                          })}
                        </>
                      );
                    })()}
                  </div>
                  
                  <div className="flex items-center space-x-2 sm:space-x-3">
                    <span className="font-medium">{selectedAssetId ? 'Asset: ' : 'Technician: '}</span>
                    {(() => {
                      const entityId = selectedResourceId || selectedAssetId;
                      const selectedResource = (() => {
                        const byResourceId = availableResources.find(r => r.resource_id === entityId);
                        if (byResourceId) return byResourceId;
                        return availableResources.find(r => r.id === entityId);
                      })();
                      
                      const isAsset = selectedResource?._isAsset;
                      const details = (!isAsset && selectedResource?.resource_id) 
                        ? resourceDetails[selectedResource.resource_id] 
                        : null;
                      
                      const resourceFirstName = details?.first_name || selectedResource?.first_name || '';
                      const resourceLastName = details?.last_name || selectedResource?.last_name || '';
                      const resourceName = details?.name || selectedResource?.name || 
                                  `${resourceFirstName} ${resourceLastName}`.trim();
                      const profilePhoto = details?.avatar_url || selectedResource?.profile_photo;
                      
                      const initials = isAsset 
                        ? (selectedResource?.name || 'A').split(/\s+/).map(w => w[0]).join('').toUpperCase().slice(0, 2)
                        : getInitials(resourceFirstName, resourceLastName);
                      
                      return (
                        <div className="flex items-center space-x-1 sm:space-x-2">
                          <div className={`w-5 h-5 sm:w-6 sm:h-6 rounded-full ${isAsset ? 'bg-orange-100 text-orange-600' : 'bg-blue-100 text-blue-600'} flex items-center justify-center font-medium overflow-hidden text-xs`}>
                            {profilePhoto ? (
                              <img src={profilePhoto} alt={initials} className="h-full w-full object-cover" />
                            ) : (
                              initials
                            )}
                          </div>
                          <span className="text-xs sm:text-sm">
                            {resourceName || 'Selected'}
                          </span>
                        </div>
                      );
                    })()}
                  </div>
                  
                  {(() => {
                    const serviceWithAddons = selectedService.priceOption.price + selectedAddons.reduce((total, addon) => total + (addon.price || 0), 0);
                    const discountAmt = bookingDiscountPercentage > 0 ? serviceWithAddons * (bookingDiscountPercentage / 100) : 0;
                    const afterDiscount = serviceWithAddons - discountAmt;
                    // Anchor the subtotal at compare-at (when set) so the discount
                    // line shows the full advertised savings, matching the service step
                    const compareAtRaw = (selectedService.priceOption as any).compare_at_price;
                    const compareSavings = compareAtRaw != null && Number(compareAtRaw) > selectedService.priceOption.price
                      ? Number(compareAtRaw) - selectedService.priceOption.price
                      : 0;
                    const anchorSubtotal = serviceWithAddons + compareSavings;
                    const totalSavings = compareSavings + discountAmt;
                    const savingsPct = totalSavings > 0 && anchorSubtotal > 0 ? Math.round((totalSavings / anchorSubtotal) * 100) : 0;
                    const businessTax = selectedService.service.tax_exempt ? 0 : afterDiscount * combinedBusinessRate;
                    const additionalTax = serviceAdditionalTaxes.reduce((sum, t) => sum + (serviceWithAddons * t.rate / 100), 0);
                    const totalTax = businessTax + additionalTax;
                    return (
                      <div className="pt-2 border-t mt-2">
                        <div className="flex items-center justify-between">
                          <span className="text-gray-600">Subtotal</span>
                          <span className="font-medium">${anchorSubtotal.toFixed(2)}</span>
                        </div>
                        
                        {totalSavings > 0 && (
                          <div className="flex items-center justify-between mt-1 text-green-600">
                            <span className="font-medium">Discount ({savingsPct}%)</span>
                            <span className="font-medium">-${totalSavings.toFixed(2)}</span>
                          </div>
                        )}
                        
                        <div className="flex items-center justify-between mt-1">
                          <span className="text-gray-600">{businessTaxLabel}</span>
                          <span className="font-medium">${businessTax.toFixed(2)}</span>
                        </div>
                        {serviceAdditionalTaxes.map((t, i) => (
                          <div key={i} className="flex items-center justify-between mt-1">
                            <span className="text-gray-600">{t.name} ({t.rate}%)</span>
                            <span className="font-medium">${(serviceWithAddons * t.rate / 100).toFixed(2)}</span>
                          </div>
                        ))}
                        <div className="flex items-center justify-between mt-1 pt-1 sm:mt-2 sm:pt-2 border-t">
                          <span className="font-semibold">Total</span>
                          <span className="font-semibold">${(afterDiscount + totalTax).toFixed(2)}</span>
                        </div>
                      </div>
                    );
                  })()}
                </>
              ) : null}
            </div>
          </div>
        )}
            
            {isResourceModalOpen && <ResourceSelectionModal />}
          </div>
        </div>
      </div>
    );
  };

  const handleSubmitBooking = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form
    const errors = {
      firstName: !firstName ? 'First name is required' : '',
      lastName: !lastName ? 'Last name is required' : '',
      vehicleYear: !vehicleYear ? 'Vehicle year is required' : '',
      vehicleMake: !vehicleMake ? 'Vehicle make is required' : '',
      vehicleModel: !vehicleModel ? 'Vehicle model is required' : '',
      email: !email ? 'Email is required' : !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) ? 'Invalid email address' : '',
      phone: !phone ? 'Phone number is required' : ''
    };
    
    setFormErrors(errors);
    
    // Check if any errors exist
    if (Object.values(errors).some(error => error)) {
      return;
    }
    
    if (!selectedService || !selectedTime || (!selectedResourceId && !selectedAssetId)) {
      toast.error('Please complete all previous steps before booking');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      if (!supabase) throw new Error('Supabase client not initialized');
      
      const startTime = new Date(selectedTime);
      const totalServiceDuration = selectedService.priceOption.duration_minutes + selectedAddons.reduce((sum, addon) => sum + (addon.duration_minutes || 0), 0);
      const endTime = new Date(startTime.getTime() + totalServiceDuration * 60000);
      
      const startDateStr = format(startTime, 'yyyy-MM-dd');
      const startTimeStr = format(startTime, 'HH:mm');
      const endDateStr = format(endTime, 'yyyy-MM-dd');
      const endTimeStr = format(endTime, 'HH:mm');
      
      // A signed-in booking account always books onto that customer.
      // A password at checkout creates or signs into the booking account first.
      // Guests keep the existing email-or-phone match.
      let customerId = '';
      let accountCustomerId = bookingCustomerId;
      if (!accountCustomerId && accountPassword.trim()) {
        if (accountPassword.trim().length < 8) {
          toast.error('Password must be at least 8 characters');
          setIsSubmitting(false);
          return;
        }
        if (emailAccountState === 'customer') {
          toast.error('This email is already on file. Create your password from the profile icon, or clear the password to book without an account.');
          setIsSubmitting(false);
          return;
        }
        try {
          if (emailAccountState === 'new') {
            const created = await bookingCheckoutSignup(businessSlug || '', email.trim(), accountPassword, firstName, lastName);
            accountCustomerId = created.customer.id;
          } else if (emailAccountState === 'account') {
            const signedIn = await portalPasswordLogin(businessSlug || '', email.trim(), accountPassword, 'booking');
            saveBookingSession(businessSlug || '', signedIn);
            accountCustomerId = signedIn.customer.id;
          } else {
            toast.error('Check the email and try again');
            setIsSubmitting(false);
            return;
          }
        } catch (accountError) {
          toast.error(accountError instanceof Error ? accountError.message : 'Could not sign in');
          setIsSubmitting(false);
          return;
        }
        setBookingCustomerId(accountCustomerId);
        bookingCustomerRef.current = accountCustomerId;
      }

      if (accountCustomerId) {
        customerId = accountCustomerId;
        await updateCustomerFromBooking(customerId, {
          first_name: firstName,
          last_name: lastName,
          phone,
          address,
        });
      } else {
      console.log('Checking for existing customer with:', {
        business_id: business?.id || '',
        email,
        phone
      });

      const existingCustomer = await findExistingCustomer(
        business?.id || '',
        email,
        phone
      );

      if (existingCustomer) {
        console.log('Found existing customer:', {
          id: existingCustomer.id,
          first_name: existingCustomer.first_name,
          last_name: existingCustomer.last_name,
          email: existingCustomer.email,
          phone: existingCustomer.phone
        });
        
        // Update the existing customer with any new information
        const updatedCustomer = await updateCustomerFromBooking(
          existingCustomer.id,
          {
            first_name: firstName,
            last_name: lastName,
            email: email,
            phone: phone,
            address: address
          }
        );
        
        customerId = existingCustomer.id;
        console.log('Updated existing customer:', updatedCustomer);
      } else {
        // Create a new customer
        console.log('No matching customer found. Creating new customer');
      const { data: customerData, error: customerError } = await supabase
        .from('customers')
        .insert({
            business_id: business?.id || '',
          first_name: firstName,
          last_name: lastName,
          email: email,
          phone: phone,
          address: address,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .select('id')
        .single();
      
      if (customerError) throw customerError;
      if (!customerData?.id) throw new Error('Failed to create customer record');
        
        customerId = customerData.id;
      }
      }

      const matchedVehicle = bookingVehicles.find((vehicle) =>
        String(vehicle.year) === String(vehicleYear).trim()
        && vehicle.make.trim().toLowerCase() === vehicleMake.trim().toLowerCase()
        && vehicle.model.trim().toLowerCase() === vehicleModel.trim().toLowerCase()
      );
      let vehicleDataId = matchedVehicle?.id || '';
      if (!vehicleDataId) {
      const { data: vehicleData, error: vehicleError } = await supabase
        .from('customer_vehicles')
        .insert({
          customer_id: customerId,
          make: vehicleMake,
          model: vehicleModel,
          year: vehicleYear,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .select('id')
        .single();
      
      if (vehicleError) throw vehicleError;
      
      if (!vehicleData?.id) throw new Error('Failed to create vehicle record');
      vehicleDataId = vehicleData.id;
      }
      
      // Create mobile booking using the createMobileBooking function
      console.log('🎯 ONLINE BOOKING: About to create booking with buffer_time:', bufferTime);
      console.log('🎯 ONLINE BOOKING: bookingDiscountPercentage:', bookingDiscountPercentage);
      console.log('🎯 ONLINE BOOKING: Service price:', selectedService.priceOption.price);
      
      const result = await createMobileBooking({
        business_id: business?.id || '',
        customer_id: customerId,
        resource_id: selectedResourceId || undefined,
        asset_id: selectedAssetId || undefined,
        title: `Online Booking - ${firstName} ${lastName}`,
        start_date: startDateStr,
        start_time: startTimeStr,
        end_date: endDateStr,
        end_time: endTimeStr,
        status: 'scheduled',
        notes: 'Created via online booking',
        customer_notes: notes || '',
        booking_address: portalAssignedAssetId ? '' : address,
        created_by: 'online', // Mark as online booking
        // Pass the buffer time to the booking creation
        buffer_time: bufferTime,
        vehicles: [
          {
            vehicle_id: vehicleDataId,
            services: [
              {
                service_id: selectedService.service.id,
                name: selectedService.service.name,
                description: selectedService.service.description || '',
                price: selectedService.priceOption.price, // Original service price
                discount_amount: 0, // Discount will be applied separately via booking_discounts table
                tax_exempt: selectedService.service.tax_exempt || false,
                tax_rate: selectedService.service.tax_exempt ? 0 : 5,
                duration_minutes: selectedService.priceOption.duration_minutes,
                addons: selectedAddons.map(addon => ({
                  addon_id: addon.id,
                  name: addon.name,
                  price: addon.price,
                  duration_minutes: addon.duration_minutes || 0
                }))
              }
            ]
          }
        ]
      });
      
      if (!result || !result.id) throw new Error('No booking data returned');
      
      // Apply booking discount percentage FIRST (if set)
      if (bookingDiscountPercentage > 0 && selectedService) {
        try {
          console.log('🎯 ONLINE BOOKING: Applying booking discount:', bookingDiscountPercentage + '%');

          // Store as a PERCENTAGE discount (not a frozen dollar amount) so the
          // booking-totals triggers and the invoice engine keep recalculating it
          // when services are added/removed after the booking is created.
          const { applyDiscount } = await import('../../lib/discounts');
          await applyDiscount(
            result.id,
            `Online Booking Discount (${bookingDiscountPercentage}%)`,
            'percentage',
            bookingDiscountPercentage
          );
          console.log('✅ ONLINE BOOKING: Booking discount applied successfully:', bookingDiscountPercentage + '%');
        } catch (discountError) {
          console.error('❌ ONLINE BOOKING: Error applying booking discount:', discountError);
          // Don't fail the booking if discount application fails
        }
      }
      
      // Apply discount code if one was entered and validated (AFTER booking discount)
      if (appliedDiscount && business?.id && selectedService) {
        try {
          console.log('🎯 ONLINE BOOKING: Applying discount code to booking:', appliedDiscount.code);
          // Calculate order amount after booking discount (including add-ons)
          const orderServiceWithAddons = selectedService.priceOption.price + selectedAddons.reduce((total, addon) => total + (addon.price || 0), 0);
          const orderAmount = bookingDiscountPercentage > 0 
            ? orderServiceWithAddons * (1 - (bookingDiscountPercentage / 100))
            : orderServiceWithAddons;
          await DiscountCodeService.applyDiscountCode(result.id, business.id, appliedDiscount.code, orderAmount);
          console.log('✅ ONLINE BOOKING: Discount code applied successfully');
        } catch (discountError) {
          console.error('❌ ONLINE BOOKING: Error applying discount code:', discountError);
          // Don't fail the booking if discount application fails
        }
      }

      if (selectedReward && !appliedDiscount && businessSlug && accountCustomerId) {
        try {
          await applyBookingReward(businessSlug, result.id, selectedReward.id);
        } catch (rewardError) {
          console.error('Reward was not applied:', rewardError);
          toast.error(rewardError instanceof Error ? rewardError.message : 'Your booking is confirmed, but the reward was not applied. Your points were not used.');
        }
      }
      
      // 🎯 Trigger booking automations after successful online booking
      // Keep a handle on the trigger promise so we don't hard-redirect (which aborts
      // in-flight requests) before the automation kickoff has completed.
      let automationTriggerPromise: Promise<void> = Promise.resolve();
      if (business?.id && result) {
        try {
          console.log('🎯 ONLINE BOOKING: Starting automation trigger process...');
          console.log('🎯 ONLINE BOOKING: Business ID:', business.id);
          console.log('🎯 ONLINE BOOKING: Booking result:', result);
          
          // Check if we can import the function correctly
          console.log('🎯 ONLINE BOOKING: triggerBookingAutomations function available:', typeof triggerBookingAutomations);
          
          // Calculate grand total (including tax and BOTH discounts)
          let subtotal = selectedService.priceOption.price + selectedAddons.reduce((total, addon) => total + (addon.price || 0), 0);
          
          // Apply booking discount first
          if (bookingDiscountPercentage > 0) {
            subtotal = subtotal * (1 - (bookingDiscountPercentage / 100));
          }
          
          subtotal = subtotal - bookingExtraOff(subtotal, appliedDiscount, selectedReward);
          
          const grandTotal = selectedService.service.tax_exempt ? 
            subtotal : 
            subtotal * 1.05;
          
          // Find the selected resource/asset details for automation
          const confirmEntityId = selectedResourceId || selectedAssetId;
          const selectedResource = (() => {
            const byResourceId = availableResources.find(r => r.resource_id === confirmEntityId);
            if (byResourceId) return byResourceId;
            return availableResources.find(r => r.id === confirmEntityId);
          })();
          
          const isAssetBooking = selectedResource?._isAsset;
          const details = (!isAssetBooking && selectedResource?.resource_id) 
            ? resourceDetails[selectedResource.resource_id] 
            : null;
          
          const resourceFirstName = details?.first_name || selectedResource?.first_name || '';
          const resourceLastName = details?.last_name || selectedResource?.last_name || '';
          
          // Use booking_number from result (createMobileBooking returns it)
          // If not available, try to fetch it, but don't fail if fetch fails
          let bookingNumber = result.booking_number;
          
          if (!bookingNumber) {
            console.log('🎯 ONLINE BOOKING: Booking number not in result, fetching from database...');
            try {
          const { data: bookingDetails, error: fetchError } = await supabase
            .from('bookings')
                .select('booking_number')
            .eq('id', result.id)
            .eq('is_deleted', false)
            .single();
          
              if (!fetchError && bookingDetails?.booking_number) {
                bookingNumber = bookingDetails.booking_number;
                console.log('🎯 ONLINE BOOKING: ✅ Fetched booking number:', bookingNumber);
              } else {
                console.warn('🎯 ONLINE BOOKING: ⚠️ Could not fetch booking number, will use result:', fetchError);
                bookingNumber = result.booking_number || 'Unknown';
              }
            } catch (fetchError) {
              console.error('🎯 ONLINE BOOKING: ❌ Error fetching booking number:', fetchError);
              bookingNumber = result.booking_number || 'Unknown';
            }
          }
          
          console.log('🎯 ONLINE BOOKING: 📋 Using booking number:', bookingNumber);
          
          // Use phone from form state directly - it's already validated and saved to DB
          // The phone was just saved to the customer record, so we can trust the form value
          const formattedPhone = phone?.trim() || '';
          
          console.log('🎯 ONLINE BOOKING: Phone validation and preparation:', {
            formPhone: phone,
            trimmedPhone: formattedPhone,
            phoneLength: formattedPhone.length,
            isValid: formattedPhone.length > 0,
            customerId: customerId,
            phoneType: typeof phone
          });
          
          // Double-check: Fetch from DB as backup if form phone is empty
          let finalPhone = formattedPhone;
          if (!finalPhone && customerId) {
            console.warn('🎯 ONLINE BOOKING: ⚠️ Form phone is empty, attempting DB fetch...');
            try {
              const { data: customerRecord, error: customerFetchError } = await supabase
                .from('customers')
                .select('phone')
                .eq('id', customerId)
                .single();
              
              if (!customerFetchError && customerRecord?.phone) {
                finalPhone = customerRecord.phone.trim();
                console.log('🎯 ONLINE BOOKING: ✅ Retrieved phone from DB backup:', finalPhone);
              } else {
                console.error('🎯 ONLINE BOOKING: ❌ DB fetch also failed:', customerFetchError);
              }
            } catch (fetchError) {
              console.error('🎯 ONLINE BOOKING: ❌ Exception during DB fetch:', fetchError);
            }
          }
          
          if (!finalPhone) {
            console.error('🎯 ONLINE BOOKING: ❌ CRITICAL ERROR - Phone number is empty! SMS automations will fail.');
            console.error('🎯 ONLINE BOOKING: Customer ID:', customerId);
            console.error('🎯 ONLINE BOOKING: Form phone value:', phone);
            console.error('🎯 ONLINE BOOKING: This booking will NOT trigger SMS automations successfully.');
            // Don't block the booking, but log the error clearly
          } else {
            console.log('🎯 ONLINE BOOKING: ✅ Phone number validated:', finalPhone);
            console.log('🎯 ONLINE BOOKING: ✅ Phone will be passed to automation:', {
              phone: finalPhone,
              length: finalPhone.length,
              type: typeof finalPhone
            });
          }
          
          // Prepare booking data for automation trigger
          // IMPORTANT: Always include phone even if empty, so we can debug why it's missing
          const realBookingData = {
            booking_id: result.id,
            booking_number: bookingNumber, // ✅ Use booking number from result or fetch
            business_id: business.id, // Add business_id for consistency
            title: `Online Booking - ${firstName} ${lastName}`,
            customer: {
              id: customerId, // Add customer ID for consistency
              name: `${firstName} ${lastName}`,
              full_name: `${firstName} ${lastName}`,
              first_name: firstName, // Add first_name for SMS action
              last_name: lastName, // Add last_name for SMS action
              phone: finalPhone, // ✅ Use validated phone (form or DB fallback)
              email: email
            },
            schedule: {
              drop_off_date: startDateStr,
              drop_off_time: startTimeStr,
              start_date: startDateStr,
              start_time: startTimeStr,
              end_date: endDateStr,
              end_time: endTimeStr
            },
            services: [{
              name: selectedService.service.name,
              price: selectedService.priceOption.price,
              duration_minutes: selectedService.priceOption.duration_minutes,
              vehicle: {
                id: vehicleData.id,
                make: vehicleMake,
                model: vehicleModel,
                year: vehicleYear
              }
            }],
            grand_total: grandTotal,
            status: 'scheduled',
            resources: [{
              name: `${resourceFirstName} ${resourceLastName}`.trim(),
              email: '', // Resource email not available in online booking
              phone: '' // Resource phone not available in online booking
            }],
            vehicles: [{
              make: vehicleMake || '',
              model: vehicleModel || '',
              year: parseInt(vehicleYear) || 0,
              color: '',
              display: `${vehicleYear || ''} ${vehicleMake || ''} ${vehicleModel || ''}`.trim()
            }],
            booking_address: portalAssignedAssetId ? '' : address,
            source: 'online_booking'
          };

          console.log('🎯 ONLINE BOOKING: Prepared booking data for automations:');
          console.log('🎯 ONLINE BOOKING: - Booking ID:', realBookingData.booking_id);
          console.log('🎯 ONLINE BOOKING: - Booking Number:', realBookingData.booking_number);
          console.log('🎯 ONLINE BOOKING: - Customer:', realBookingData.customer);
          console.log('🎯 ONLINE BOOKING: - Customer Phone:', realBookingData.customer?.phone);
          console.log('🎯 ONLINE BOOKING: - Customer Phone Type:', typeof realBookingData.customer?.phone);
          console.log('🎯 ONLINE BOOKING: - Customer Phone Value:', JSON.stringify(realBookingData.customer?.phone));
          console.log('🎯 ONLINE BOOKING: - Customer ID:', realBookingData.customer?.id);
          console.log('🎯 ONLINE BOOKING: - Schedule:', realBookingData.schedule);
          console.log('🎯 ONLINE BOOKING: - Services:', realBookingData.services);
          console.log('🎯 ONLINE BOOKING: Full booking data:', JSON.stringify(realBookingData, null, 2));
          
          console.log('🎯 ONLINE BOOKING: Calling triggerBookingAutomations with:');
          console.log('🎯 ONLINE BOOKING: - Business ID:', business.id);
          console.log('🎯 ONLINE BOOKING: - Event Type: "created"');
          console.log('🎯 ONLINE BOOKING: - Data keys:', Object.keys(realBookingData));
          
          // Add timestamp for debugging
          const triggerStartTime = Date.now();
          console.log('🎯 ONLINE BOOKING: ⏰ Automation trigger started at:', new Date().toISOString());
          console.log('🎯 ONLINE BOOKING: 📤 Sending to triggerBookingAutomations:', {
            businessId: business.id,
            eventType: 'created',
            bookingId: realBookingData.booking_id,
            bookingNumber: realBookingData.booking_number,
            hasCustomer: !!realBookingData.customer,
            customerPhone: realBookingData.customer?.phone,
            customerId: realBookingData.customer?.id
          });
          
          // Run automations in the background (doesn't block UI)
          automationTriggerPromise = triggerBookingAutomations(business.id, 'created', realBookingData)
            .then(() => {
              const triggerEndTime = Date.now();
              const duration = triggerEndTime - triggerStartTime;
              console.log('🎯 ONLINE BOOKING: ✅ Booking automations triggered successfully');
              console.log('🎯 ONLINE BOOKING: ⏱️ Trigger duration:', duration + 'ms');
            })
            .catch((triggerError: any) => {
              const triggerEndTime = Date.now();
              const duration = triggerEndTime - triggerStartTime;
              console.error('🎯 ONLINE BOOKING: ❌ Error executing booking automations:', triggerError);
              console.error('🎯 ONLINE BOOKING: ⏱️ Failed after:', duration + 'ms');
              console.error('🎯 ONLINE BOOKING: ❌ Error details:', {
                name: triggerError?.name || 'Unknown',
                message: triggerError?.message || 'No message available',
                stack: triggerError?.stack || 'No stack trace available'
              });
            });
          
          console.log('🎯 ONLINE BOOKING: Automation trigger call initiated (async)');
        } catch (triggerError) {
          console.error('❌ ONLINE BOOKING: Failed to trigger automations:', triggerError);
          console.error('❌ ONLINE BOOKING: Error details:', {
            name: (triggerError as any)?.name || 'Unknown',
            message: (triggerError as any)?.message || 'No message available',
            stack: (triggerError as any)?.stack || 'No stack trace available'
          });
          // Don't throw here to avoid blocking the booking flow
        }
      } else {
        console.warn('🎯 ONLINE BOOKING: ⚠️ Cannot trigger automations - missing business ID or booking result');
        console.warn('🎯 ONLINE BOOKING: - Business ID present:', !!business?.id);
        console.warn('🎯 ONLINE BOOKING: - Booking result present:', !!result);
      }
      
      // Track completion step
      if (business?.id) {
        await trackStepProgression(business.id, 'completed');
      }
      
      // Success
      toast.success('Booking confirmed!');
      
      // Redirect to success page (custom URL or default).
      // window.location.href is a hard navigation that cancels any in-flight requests,
      // so wait for the automation trigger to finish (capped at 8s) and show the
      // confirmation toast for at least 1.5s before leaving the page.
      const minDisplayDelay = new Promise((resolve) => setTimeout(resolve, 1500));
      const automationWaitCap = new Promise((resolve) => setTimeout(resolve, 8000));
      Promise.all([minDisplayDelay, Promise.race([automationTriggerPromise, automationWaitCap])])
        .then(() => {
        if (customThankYouUrl) {
          // Use custom thank you URL if configured
          console.log('🔗 Redirecting to custom thank you URL:', customThankYouUrl);
          window.location.href = customThankYouUrl;
        } else {
          // Use default booking success page
        window.location.href = `/${businessSlug}/booking-success?booking=${result.id}`;
        }
        });
      
    } catch (err) {
      console.error('Error creating booking:', err);
      toast.error('Failed to create booking. Please try again.');
      setIsSubmitting(false);
    }
  };

  const renderCustomerStep = () => {
    if (!selectedTime || (!selectedResourceId && !selectedAssetId) || !selectedService) {
    return (
        <div className="text-center py-8">
          <p className="text-red-600">Please complete all previous steps before entering customer information.</p>
          <button 
            onClick={() => setCurrentStep('datetime')}
            className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
          >
            Go Back to Date & Time Selection
          </button>
        </div>
      );
    }
    
    // Find the selected resource or asset
    const entityId = selectedResourceId || selectedAssetId;
    const selectedResource = (() => {
      const byResourceId = availableResources.find(r => r.resource_id === entityId);
      if (byResourceId) return byResourceId;
      return availableResources.find(r => r.id === entityId);
    })();
    
    const isSelectedAsset = selectedResource?._isAsset;
    const details = (!isSelectedAsset && selectedResource?.resource_id) 
      ? resourceDetails[selectedResource.resource_id] 
      : null;
    
    const resourceFirstName = details?.first_name || selectedResource?.first_name || '';
    const resourceLastName = details?.last_name || selectedResource?.last_name || '';
    const resourceName = details?.name || selectedResource?.name || 
                        `${resourceFirstName} ${resourceLastName}`.trim();
    const profilePhoto = details?.avatar_url || selectedResource?.profile_photo;
    
    const startTime = new Date(selectedTime);
    const totalDur = selectedService.priceOption.duration_minutes + selectedAddons.reduce((sum, addon) => sum + (addon.duration_minutes || 0), 0);
    const endTime = new Date(startTime.getTime() + totalDur * 60000);
    const initials = isSelectedAsset
      ? (selectedResource?.name || 'A').split(/\s+/).map(w => w[0]).join('').toUpperCase().slice(0, 2)
      : getInitials(resourceFirstName, resourceLastName);

    // Grand total for the price-carrying confirm CTA (same math as the summary)
    const ctaServiceWithAddons = selectedService.priceOption.price + selectedAddons.reduce((total, addon) => total + (addon.price || 0), 0);
    let ctaNet = bookingDiscountPercentage > 0
      ? ctaServiceWithAddons * (1 - (bookingDiscountPercentage / 100))
      : ctaServiceWithAddons;
    ctaNet = ctaNet - bookingExtraOff(ctaNet, appliedDiscount, selectedReward);
    const ctaBusinessTax = selectedService.service.tax_exempt ? 0 : ctaNet * combinedBusinessRate;
    const ctaAdditionalTax = serviceAdditionalTaxes.reduce((sum, t) => sum + (ctaServiceWithAddons * t.rate / 100), 0);
    const ctaGrandTotal = ctaNet + ctaBusinessTax + ctaAdditionalTax;
    const passwordPending = !bookingCustomerId && accountPassword.trim().length > 0;
    const ctaVerb = passwordPending && emailAccountState === 'new'
      ? 'Create account and book'
      : passwordPending && emailAccountState === 'account'
        ? 'Sign in and book'
        : 'Confirm booking';

    return (
      <div className="max-w-3xl mx-auto px-3 sm:px-0">
        <div className="bg-white bg-opacity-95 backdrop-blur-md rounded-2xl shadow-2xl p-6">
          {/* Header: back affordance + value-first headline (matches earlier steps) */}
          <div className="mb-4">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentStep('datetime')}
                className="p-1.5 -ml-1.5 text-gray-400 hover:text-gray-600 rounded-full transition-colors touch-manipulation"
                aria-label="Go back"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <h2 className="text-[19px] font-semibold text-gray-900 leading-snug">Almost done</h2>
            </div>
            <p className="text-sm text-gray-500 mt-1">Review your booking and enter your details to confirm.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-8">
            {/* Customer Information Form — first on mobile, right column on desktop */}
            <div className="order-1 md:order-2">
              <form onSubmit={handleSubmitBooking} className="space-y-3 sm:space-y-4">
              <div className="grid grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <label htmlFor="firstName" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                    First Name*
                  </label>
                  <input
                    id="firstName"
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className={`block w-full rounded-[10px] border ${formErrors.firstName ? 'border-red-300' : 'border-gray-300'} py-2.5 px-3.5 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-[15px]`}
                    placeholder="Your first name"
                  />
                  {formErrors.firstName && (
                    <p className="mt-1 text-xs text-red-600">{formErrors.firstName}</p>
                  )}
                </div>
                
                <div>
                  <label htmlFor="lastName" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                    Last Name*
                  </label>
                  <input
                    id="lastName"
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className={`block w-full rounded-[10px] border ${formErrors.lastName ? 'border-red-300' : 'border-gray-300'} py-2.5 px-3.5 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-[15px]`}
                    placeholder="Your last name"
                  />
                  {formErrors.lastName && (
                    <p className="mt-1 text-xs text-red-600">{formErrors.lastName}</p>
                  )}
                </div>
              </div>

              {bookingVehicles.length > 1 && (
                <div>
                  <label htmlFor="savedVehicle" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                    Vehicle on file
                  </label>
                  <select
                    id="savedVehicle"
                    className="block w-full rounded-[10px] border border-gray-300 py-2.5 px-3.5 text-[15px] bg-white"
                    value={bookingVehicles.find((vehicle) =>
                      String(vehicle.year) === String(vehicleYear)
                      && vehicle.make === vehicleMake
                      && vehicle.model === vehicleModel
                    )?.id || ''}
                    onChange={(event) => {
                      const vehicle = bookingVehicles.find((item) => item.id === event.target.value);
                      if (!vehicle) return;
                      setVehicleYear(String(vehicle.year || ''));
                      setVehicleMake(vehicle.make || '');
                      setVehicleModel(vehicle.model || '');
                    }}
                  >
                    {bookingVehicles.map((vehicle) => (
                      <option key={vehicle.id} value={vehicle.id}>
                        {vehicle.year} {vehicle.make} {vehicle.model}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              
              <div className="grid grid-cols-3 gap-3 sm:gap-4">
                <div>
                  <label htmlFor="vehicleYear" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                    Year*
                  </label>
                  <input
                    id="vehicleYear"
                    type="text"
                    value={vehicleYear}
                    onChange={(e) => setVehicleYear(e.target.value)}
                    className={`block w-full rounded-[10px] border ${formErrors.vehicleYear ? 'border-red-300' : 'border-gray-300'} py-2.5 px-3.5 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-[15px]`}
                    placeholder="Year"
                  />
                  {formErrors.vehicleYear && (
                    <p className="mt-1 text-xs text-red-600">{formErrors.vehicleYear}</p>
                  )}
                </div>
                
                <div>
                  <label htmlFor="vehicleMake" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                    Make*
                  </label>
                  <input
                    id="vehicleMake"
                    type="text"
                    value={vehicleMake}
                    onChange={(e) => setVehicleMake(e.target.value)}
                    className={`block w-full rounded-[10px] border ${formErrors.vehicleMake ? 'border-red-300' : 'border-gray-300'} py-2.5 px-3.5 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-[15px]`}
                    placeholder="Make"
                  />
                  {formErrors.vehicleMake && (
                    <p className="mt-1 text-xs text-red-600">{formErrors.vehicleMake}</p>
                  )}
                </div>
                
                <div>
                  <label htmlFor="vehicleModel" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                    Model*
                  </label>
                  <input
                    id="vehicleModel"
                    type="text"
                    value={vehicleModel}
                    onChange={(e) => setVehicleModel(e.target.value)}
                    className={`block w-full rounded-[10px] border ${formErrors.vehicleModel ? 'border-red-300' : 'border-gray-300'} py-2.5 px-3.5 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-[15px]`}
                    placeholder="Model"
                  />
                  {formErrors.vehicleModel && (
                    <p className="mt-1 text-xs text-red-600">{formErrors.vehicleModel}</p>
                  )}
                </div>
              </div>
              
              <div>
                <label htmlFor="email" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                  Email Address*
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  readOnly={!!bookingCustomerId}
                  onChange={(e) => setEmail(e.target.value)}
                  className={`block w-full rounded-[10px] border ${formErrors.email ? 'border-red-300' : 'border-gray-300'} py-2.5 px-3.5 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-[15px] ${bookingCustomerId ? 'bg-gray-50' : ''}`}
                />
                {formErrors.email && (
                  <p className="mt-1 text-xs text-red-600">{formErrors.email}</p>
                )}
              </div>

              {!bookingCustomerId && (
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label htmlFor="accountPassword" className="block text-xs sm:text-sm font-medium text-gray-700">
                      Password <span className="text-gray-500">(optional)</span>
                    </label>
                    <button
                      type="button"
                      onClick={() => openAccountRef.current?.('login')}
                      className="text-xs font-medium text-blue-600"
                    >
                      Sign in
                    </button>
                  </div>
                  <input
                    id="accountPassword"
                    type="password"
                    value={accountPassword}
                    autoComplete="new-password"
                    onChange={(e) => setAccountPassword(e.target.value)}
                    placeholder="Create an account with this booking"
                    className="block w-full rounded-[10px] border border-gray-300 py-2.5 px-3.5 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-[15px]"
                  />
                  {emailAccountState === 'account' && accountPassword.trim() && (
                    <p className="mt-1 text-xs text-gray-600">This email already has an account. We'll sign you in and book.</p>
                  )}
                  {emailAccountState === 'customer' && accountPassword.trim() && (
                    <p className="mt-1 text-xs text-gray-600">This email is already on file. Use Sign in to create your password with the code we email you, or clear this field to book without an account.</p>
                  )}
                </div>
              )}
              
              <div>
                <label htmlFor="phone" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                  Phone Number*
                </label>
                <input
                  id="phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className={`block w-full rounded-[10px] border ${formErrors.phone ? 'border-red-300' : 'border-gray-300'} py-2.5 px-3.5 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-[15px]`}
                />
                {formErrors.phone && (
                  <p className="mt-1 text-xs text-red-600">{formErrors.phone}</p>
                )}
              </div>
              
              <div>
                <label htmlFor="notes" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                  Additional Notes <span className="text-gray-500">(optional)</span>
                </label>
                <textarea
                  id="notes"
                  rows={2}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="block w-full rounded-[10px] border border-gray-300 py-2.5 px-3.5 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-[15px]"
                  placeholder="Any special instructions for your service provider"
                />
              </div>
              
              <div>
                {!showDiscountField && !discountCode ? (
                  // Collapsed: just a text affordance — the field appears on tap
                  <button
                    type="button"
                    onClick={() => setShowDiscountField(true)}
                    className="text-[13px] font-medium text-blue-600 hover:text-blue-800 transition-colors touch-manipulation"
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    Discount Code
                  </button>
                ) : (
                <>
                <label htmlFor="discountCode" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                  Discount Code <span className="text-gray-500">(optional)</span>
                </label>
                <div className="relative">
                  <input
                    id="discountCode"
                    type="text"
                    value={discountCode}
                    onChange={(e) => {
                      const code = e.target.value.toUpperCase();
                      setDiscountCode(code);
                      // Validate after a short delay to avoid too many API calls
                      setTimeout(() => validateDiscountCode(code), 500);
                    }}
                    className={`block w-full rounded-[10px] border ${discountError ? 'border-red-300' : appliedDiscount ? 'border-green-300' : 'border-gray-300'} py-2.5 px-3.5 pr-10 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-[15px]`}
                    placeholder="Enter discount code"
                  />
                  {discountValidating && (
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-500 border-t-transparent"></div>
                    </div>
                  )}
                  {appliedDiscount && !discountValidating && (
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                      <svg className="h-4 w-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                </div>
                {discountError && (
                  <p className="mt-1 text-xs text-red-600">{discountError}</p>
                )}
                {appliedDiscount && (
                  <p className="mt-1 text-xs text-green-600">
                    ✓ {appliedDiscount.type === 'percentage' ? `${appliedDiscount.value}% off` : `$${appliedDiscount.value.toFixed(2)} off`}
                    {appliedDiscount.description && ` - ${appliedDiscount.description}`}
                  </p>
                )}
                </>
                )}
              </div>

              {bookingCustomerId && rewardCatalog.length > 0 && !appliedDiscount && (
                <div>
                  <p className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                    Use points <span className="text-gray-500">({pointBalance.toLocaleString()} available)</span>
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => setSelectedReward(null)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border ${!selectedReward ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300'}`}
                    >
                      None
                    </button>
                    {rewardCatalog.map((reward) => {
                      const affordable = pointBalance >= reward.points_cost;
                      const on = selectedReward?.id === reward.id;
                      return (
                        <button
                          key={reward.id}
                          type="button"
                          disabled={!affordable}
                          onClick={() => {
                            setSelectedReward(reward);
                            setAppliedDiscount(null);
                            setDiscountCode('');
                            setDiscountError('');
                          }}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium border disabled:opacity-40 ${on ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300'}`}
                        >
                          {reward.name} · {reward.points_cost.toLocaleString()} pts
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              
              <div className="pt-2 sm:pt-4">
                {/* Price-carrying CTA + risk reversal under the final commitment */}
                <button
                  type="submit"
                  disabled={isSubmitting || !firstName || !lastName || !vehicleYear || !vehicleMake || !vehicleModel || !email || !phone || (passwordPending && !(emailAccountState === 'new' || emailAccountState === 'account'))}
                  className="w-full py-3.5 px-6 rounded-xl font-semibold text-[15px] text-white bg-blue-600 hover:bg-blue-700 shadow-lg transition-all duration-200 active:scale-[0.98] touch-manipulation disabled:bg-gray-100 disabled:text-gray-400 disabled:shadow-none disabled:cursor-not-allowed"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  {isSubmitting ? 'Confirming booking...' : `${ctaVerb} — $${ctaGrandTotal.toFixed(2)}`}
                </button>
                <p className="mt-2.5 text-center text-[12px] text-gray-400">
                  No payment required today · We'll confirm by email and text
                </p>
              </div>
            </form>
          </div>
          
          {/* Booking Summary — below the form on mobile (collapsed by default,
              tap to expand); always expanded in the desktop left column */}
          <div className="rounded-xl border border-gray-200 bg-gray-50/80 p-4 sm:p-5 order-2 md:order-1">
            <button
              type="button"
              onClick={() => setSummaryExpanded((v) => !v)}
              aria-expanded={summaryExpanded}
              className="w-full flex items-center justify-between gap-2 md:pointer-events-none text-left touch-manipulation"
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              <h3 className="text-[15px] font-semibold text-gray-900">Booking Summary</h3>
              <span className="flex items-center gap-2 md:hidden">
                <span className="text-[15px] font-semibold text-blue-600">${ctaGrandTotal.toFixed(2)}</span>
                <svg
                  className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${summaryExpanded ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </span>
            </button>
            
            <div className={`space-y-3 sm:space-y-4 mt-3 ${summaryExpanded ? 'block' : 'hidden'} md:block`}>
              <div className="pb-2 sm:pb-3 border-b border-gray-200">
                <h4 className="text-base sm:text-lg font-medium">{selectedService.service.name}</h4>
                
                {/* Base Service Details */}
                <div className="text-gray-600 text-xs sm:text-sm mt-1">
                  <span>{selectedService.priceOption.duration_minutes} minutes • </span>
                  {(() => {
                    const basePrice = selectedService.priceOption.price;
                    const pct = bookingDiscountPercentage;
                    const net = pct > 0 ? basePrice * (1 - pct / 100) : basePrice;
                    const compareAt = (selectedService.priceOption as any).compare_at_price;
                    const hasCompare = compareAt != null && Number(compareAt) > basePrice;
                    const struck = hasCompare ? Number(compareAt) : (pct > 0 ? basePrice : null);
                    if (struck == null) {
                      return (<span>${basePrice.toFixed(2)}</span>);
                    }
                    // True savings vs the struck price (compare-at when set, else base).
                    const savingsPct = struck > 0 ? Math.round(((struck - net) / struck) * 100) : 0;
                    return (
                      <>
                        <span className="line-through text-gray-400">${Number(struck).toFixed(2)}</span>
                        <span className="ml-2 text-green-600 font-semibold">
                          ${net.toFixed(2)}
                        </span>
                        {savingsPct > 0 && (<span className="ml-1 text-xs text-green-600">({savingsPct}% off)</span>)}
                      </>
                    );
                  })()}
                </div>
                
                {/* Show selected add-ons with individual pricing and duration */}
                {selectedAddons.length > 0 && (
                  <div className="mt-3 space-y-2">
                    <div className="text-xs font-medium text-gray-700">Add-ons:</div>
                    {selectedAddons.map(addon => (
                      <div key={addon.id} className="flex items-center justify-between text-xs text-gray-600 ml-2 bg-blue-50 px-2 py-1 rounded">
                        <span>• {addon.name}</span>
                        <span>{addon.duration_minutes || 0} min • ${addon.price.toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Total Summary */}
                <div className="mt-3 pt-2 border-t border-gray-300">
                  <div className="text-xs font-medium text-gray-800">
                    Total: {selectedService.priceOption.duration_minutes + selectedAddons.reduce((total, addon) => total + (addon.duration_minutes || 0), 0)} minutes • ${(selectedService.priceOption.price + selectedAddons.reduce((total, addon) => total + (addon.price || 0), 0)).toFixed(2)}
                  </div>
                </div>
              </div>
              
              <div className="flex items-center space-x-2 sm:space-x-3">
                <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full ${isSelectedAsset ? 'bg-orange-100 text-orange-600' : 'bg-blue-100 text-blue-600'} flex items-center justify-center font-medium overflow-hidden`}>
                  {profilePhoto ? (
                    <img src={profilePhoto} alt={initials} className="h-full w-full object-cover" />
                  ) : (
                    initials
                  )}
                </div>
                <div>
                  <div className="font-medium text-sm sm:text-base">
                    {resourceName || 'Selected'}
                  </div>
                  <div className="text-xs sm:text-sm text-gray-600">{isSelectedAsset ? (selectedResource?._assetType ? selectedResource._assetType.charAt(0).toUpperCase() + selectedResource._assetType.slice(1) : 'Asset') : 'Your technician'}</div>
                </div>
              </div>
              
              <div>
                <div className="text-gray-600 text-xs sm:text-sm">Date</div>
                <div className="font-medium text-sm sm:text-base">
                  {startTime.toLocaleDateString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </div>
              </div>
              
              <div>
                <div className="text-gray-600 text-xs sm:text-sm">Time</div>
                <div className="font-medium text-sm sm:text-base">
                  {startTime.toLocaleTimeString('en-US', {
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true
                  })} - {endTime.toLocaleTimeString('en-US', {
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true
                  })}
                </div>
              </div>
              
              <div>
                <div className="text-gray-600 text-xs sm:text-sm">Location</div>
                <div className="font-medium text-sm sm:text-base">{address}</div>
              </div>
              
              <div className="pt-2 sm:pt-3 border-t border-gray-200">
                {/* Subtotal is anchored at the compare-at price (when set) so the
                    discount line below reflects the full advertised savings:
                    compare-at difference + the actual online discount */}
                {(() => {
                  const addonsTotal = selectedAddons.reduce((total, addon) => total + (addon.price || 0), 0);
                  const basePrice = selectedService.priceOption.price;
                  const serviceWithAddons = basePrice + addonsTotal;
                  const compareAt = (selectedService.priceOption as any).compare_at_price;
                  const compareSavings = compareAt != null && Number(compareAt) > basePrice ? Number(compareAt) - basePrice : 0;
                  const anchorSubtotal = serviceWithAddons + compareSavings;
                  const pctDiscountAmt = bookingDiscountPercentage > 0 ? serviceWithAddons * (bookingDiscountPercentage / 100) : 0;
                  const totalSavings = compareSavings + pctDiscountAmt;
                  const savingsPct = totalSavings > 0 && anchorSubtotal > 0 ? Math.round((totalSavings / anchorSubtotal) * 100) : 0;
                  return (
                    <>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600 text-xs sm:text-sm">Subtotal</span>
                        <span className="font-medium text-sm sm:text-base">${anchorSubtotal.toFixed(2)}</span>
                      </div>
                      {totalSavings > 0 && (
                        <div className="flex items-center justify-between mt-1 sm:mt-2">
                          <span className="text-green-600 text-xs sm:text-sm">
                            Discount ({savingsPct}%)
                          </span>
                          <span className="font-medium text-sm sm:text-base text-green-600">
                            -${totalSavings.toFixed(2)}
                          </span>
                        </div>
                      )}
                    </>
                  );
                })()}
                
                {/* Discount code row (additional discount from promo code) */}
                {selectedReward && !appliedDiscount && (
                  <div className="flex items-center justify-between mt-1 sm:mt-2">
                    <span className="text-green-600 text-xs sm:text-sm">
                      {selectedReward.name}
                    </span>
                    <span className="font-medium text-sm sm:text-base text-green-600">
                      -${(() => {
                        const serviceWithAddons = selectedService.priceOption.price + selectedAddons.reduce((total, addon) => total + (addon.price || 0), 0);
                        const afterBookingDiscount = bookingDiscountPercentage > 0
                          ? serviceWithAddons * (1 - (bookingDiscountPercentage / 100))
                          : serviceWithAddons;
                        return bookingExtraOff(afterBookingDiscount, null, selectedReward).toFixed(2);
                      })()}
                    </span>
                  </div>
                )}
                {appliedDiscount && (
                  <div className="flex items-center justify-between mt-1 sm:mt-2">
                    <span className="text-green-600 text-xs sm:text-sm">
                      Discount Code ({appliedDiscount.code})
                    </span>
                    <span className="font-medium text-sm sm:text-base text-green-600">
                      -${(() => {
                        const serviceWithAddons = selectedService.priceOption.price + selectedAddons.reduce((total, addon) => total + (addon.price || 0), 0);
                        // Apply booking discount first
                        const afterBookingDiscount = bookingDiscountPercentage > 0 
                          ? serviceWithAddons * (1 - (bookingDiscountPercentage / 100))
                          : serviceWithAddons;
                        // Then apply discount code on top
                        const codeDiscountAmount = appliedDiscount.type === 'percentage' 
                          ? afterBookingDiscount * (appliedDiscount.value / 100)
                          : appliedDiscount.value;
                        return Math.min(codeDiscountAmount, afterBookingDiscount).toFixed(2);
                      })()}
                    </span>
                  </div>
                )}
                
                {(() => {
                  const serviceWithAddons = selectedService.priceOption.price + selectedAddons.reduce((total, addon) => total + (addon.price || 0), 0);
                  let subtotal = bookingDiscountPercentage > 0 
                    ? serviceWithAddons * (1 - (bookingDiscountPercentage / 100))
                    : serviceWithAddons;
                  subtotal = subtotal - bookingExtraOff(subtotal, appliedDiscount, selectedReward);
                  const businessTax = selectedService.service.tax_exempt ? 0 : subtotal * combinedBusinessRate;
                  const additionalTax = serviceAdditionalTaxes.reduce((sum, t) => sum + (serviceWithAddons * t.rate / 100), 0);
                  const totalTax = businessTax + additionalTax;
                  return (
                    <>
                      <div className="flex items-center justify-between mt-1 sm:mt-2">
                        <span className="text-gray-600 text-xs sm:text-sm">{businessTaxLabel}</span>
                        <span className="font-medium text-sm sm:text-base">${businessTax.toFixed(2)}</span>
                      </div>
                      {serviceAdditionalTaxes.map((t, i) => (
                        <div key={i} className="flex items-center justify-between mt-1 sm:mt-2">
                          <span className="text-gray-600 text-xs sm:text-sm">{t.name} ({t.rate}%)</span>
                          <span className="font-medium text-sm sm:text-base">${(serviceWithAddons * t.rate / 100).toFixed(2)}</span>
                        </div>
                      ))}
                      <div className="flex items-center justify-between mt-1 sm:mt-2 pt-1 sm:pt-2 border-t">
                        <span className="font-semibold text-sm sm:text-lg">Total</span>
                        <span className="font-semibold text-sm sm:text-lg">${(subtotal + totalTax).toFixed(2)}</span>
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
        </div>
      </div>
    );
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 'location':
        return renderLocationStep();
      case 'category':
        return renderCategoryStep();
      case 'service':
        return renderServiceStep();
      case 'datetime':
        return renderDateTimeStep();
      case 'customer':
        return renderCustomerStep();
      default:
        return null;
    }
  };

  // ── Step transition direction ───────────────────────────────
  // Each phase gets an index (add-ons is a half-step inside 'service') so a
  // keyed wrapper can slide the new content in from the right going forward
  // and from the left going back — instead of an instant swap.
  const STEP_ORDER: Step[] = ['location', 'category', 'service', 'datetime', 'customer'];
  const phaseIndex = STEP_ORDER.indexOf(currentStep) + (currentStep === 'service' && showAddonsModal ? 0.5 : 0);
  const stepDirection = phaseIndex >= prevPhaseIndexRef.current ? 'forward' : 'back';
  useEffect(() => {
    prevPhaseIndexRef.current = phaseIndex;
  }, [phaseIndex]);

  if (isLoading) {
    return <div className="min-h-screen bg-white"></div>;
  }

  if (error || !business) {
    return <div>Error: {error || 'Business not found'}</div>;
  }

  if (!portalResolved) {
    return <div className="min-h-screen bg-white"></div>;
  }

  return (
    <GoogleMapsLoader 
      apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY || ''}
      onLoad={() => setIsMapLoaded(true)}
    >
      {/* Fixed to the viewport: the page itself never scrolls — each step's
          content area scrolls internally instead */}
      <div
        className="h-screen bg-white flex flex-col relative overflow-hidden"
        style={{
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
          height: '100dvh',
        }}
      >
        {/* Google Map Background */}
        <div 
          ref={mapContainerRef}
          className="absolute inset-0 z-0"
          style={{ width: '100%', height: '100%' }}
        />
      
      {/* Map animation indicator */}
      {isMapAnimating && (
        <div className="absolute top-32 left-1/2 transform -translate-x-1/2 bg-white px-6 py-3 rounded-full shadow-lg z-50 flex items-center space-x-2">
          <div className="w-2 h-2 bg-blue-500 rounded-full animate-ping"></div>
          <span className="text-sm font-medium text-gray-700">📍 Locating your address...</span>
        </div>
      )}
      
      
      {/* Popup Notification */}
      <BookingPagePopup
        isEnabled={popupSettings.popup_enabled}
        delaySeconds={popupSettings.popup_delay_seconds}
        durationSeconds={popupSettings.popup_duration_seconds}
        message={popupSettings.popup_text}
        buttonText={popupSettings.popup_button_text}
        buttonAction={popupSettings.popup_button_action}
        buttonUrl={popupSettings.popup_button_url}
        sendEmail={popupSettings.popup_send_email}
        emailTemplateId={popupSettings.popup_email_template_id}
        businessId={business?.id || ''}
      />
      
      {/* Top bar */}
      <div 
        className="h-24 w-full flex items-center justify-between px-6 sm:px-8 relative z-10" 
        style={{ 
          backgroundColor: business?.booking_settings?.navbar_color || '#000000',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
        }}
      >
        {/* Logo Section */}
        <div className="flex items-center">
          {business?.booking_settings?.logo_url && (
            <div className="flex items-center justify-center py-2">
              <img 
                src={business.booking_settings.logo_url} 
                alt={`${business.name} logo`}
                className="h-16 sm:h-[72px] md:h-20 max-w-[300px] sm:max-w-[400px] object-contain"
                style={{
                  maxHeight: '80px', // Use most of the header space (24 = 96px)
                  filter: business.booking_settings.logo_invert 
                    ? 'brightness(0) invert(1) drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1))' 
                    : 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1))'
                }}
                onError={(e) => {
                  // Hide the image if it fails to load
                  (e.target as HTMLImageElement).style.display = 'none';
                  console.error('Failed to load business logo:', business.booking_settings.logo_url);
                }}
              />
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
        {/* Call sits left of the customer icon when the call button is on. */}
        {popupSettings.call_button_enabled && business?.phone && (
          <div className="flex items-center">
            <a
              href={`tel:${business.phone}`}
              className="inline-flex items-center px-4 py-2 bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded-lg transition-all duration-200 active:scale-95 backdrop-blur-sm"
              style={{
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.2)'
              }}
            >
              <svg 
                className="w-4 h-4 mr-2" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" 
                />
              </svg>
              <span className="text-sm font-medium">Call</span>
            </a>
          </div>
        )}
          {businessSlug && (
            <BookingAccountButton
              slug={businessSlug}
              prefillEmail={email}
              onCustomer={handleBookingCustomer}
              onOpenReady={(open) => { openAccountRef.current = open; }}
            />
          )}
        </div>
      </div>
      
      <div className="flex-1 min-h-0 flex flex-col justify-between py-6 relative z-10 overflow-hidden">
        {/* Top Section - Title and Form */}
        <div className="w-full px-6 sm:px-8 flex-1 min-h-0 flex flex-col">
          {/* Simple segmented progress bar (same pattern as the signup flow) */}
          <div className="max-w-2xl mx-auto mb-4">
            <div className="flex gap-1.5">
              {steps.map((item, index) => {
                const currentIdx = steps.findIndex(s => s.step === currentStep);
                const fill = index < currentIdx ? 1 : index === currentIdx ? 0.5 : 0;
                return (
                  <div key={item.step} className="h-1 flex-1 rounded-full bg-white bg-opacity-60 overflow-hidden">
                    <div
                      className="h-full rounded-full bg-blue-600 transition-all duration-300"
                      style={{ width: `${fill * 100}%` }}
                    />
                  </div>
                );
              })}
            </div>
          </div>

          {/* Form Content — keyed per phase so each step slides in
              (from the right going forward, from the left going back).
              Scrolls internally: content taller/wider than the screen stays
              inside this container instead of scrolling the whole page. */}
          <div
            key={phaseIndex}
            className={`max-w-2xl w-full mx-auto flex-1 min-h-0 overflow-y-auto overflow-x-hidden overscroll-contain ${stepDirection === 'forward' ? 'animate-stepForward' : 'animate-stepBack'}`}
            style={{ WebkitOverflowScrolling: 'touch' }}
          >
            {renderStepContent()}
          </div>

          {/* Persistent Google reviews badge (hidden on the intake form step) */}
          {currentStep !== 'customer' && renderGoogleReviewsBadge()}
        </div>

        {/* The old floating NEXT button is gone — every step now has its own
            in-card CTA (location, datetime) or advances on tap (category, service). */}
        </div>
        
        {/* Add-ons Selection Modal */}
      </div>
    </GoogleMapsLoader>
  );
} 